<template>
  <div
    v-if="mustFilterByClientOrUser"
    class="q-my-lg full-width text-center row q-col-gutter-lg"
  >
    <div class="col-12">
      <span class="items-center">
        <q-icon name="warning" />
        {{ $t('sessionActivityReports.noClientOrUserFilter') }}
      </span>
    </div>
    <div class="col-12">
      <q-btn
        flat
        color="dark"
        :label="$t('sessionActivityReports.buttonAddClientFilterLabel')"
        @click="onAddFilter('client')"
      />
      <q-btn
        flat
        color="dark"
        :label="$t('sessionActivityReports.buttonAddUserFilterLabel')"
        @click="onAddFilter('userName')"
      />
    </div>
  </div>
  <q-table
    v-else
    v-model:pagination="pagination"
    :rows="paginatedSessionActivities"
    :columns="visibleColumns"
    :loading="loading || isLoadingNewSessions"
    flat
    :bordered="bordered"
    :pagination-label="hidePaginationLabel ? () => ('') : undefined"
    row-key="id"
    :selection="selection"
    :selected="selectedRows"
    :rows-per-page-options="rowsPerPageOptions"
    :virtual-scroll="virtualScroll"
    :class="{
      'virtual-scroll-height': virtualScroll && hasSessionActivities,
    }"
    :no-data-label="noDataLabel"
    class="q-mt-md sticky-header sticky-last-column"
    @update:selected="onSelect"
    @request="onRequest"
  >
    <template
      v-if="!hideBulkActions"
      #top
    >
      <div
        class="row q-py-xs full-width items-stretch"
      >
        <div
          class="q-mr-md col self-center"
        >
          <div class="row col-sm col-xs-12 q-col-gutter-xs">
            <filter-chips-bar
              :filters="accessibleFilters"
              :filter-criteria="filterCriteria"
              :filter-groups="columnGroups"
              :before-open="cacheSessionNoteFields"
              @update:filters="onFiltersChanged"
            />
          </div>
        </div>
        <div
          class="row q-py-xs col-auto"
          :class="{
            'q-col-gutter-sm': $q.screen.gt.sm,
            'q-col-gutter-xs': !$q.screen.gt.sm,
          }"
        >
          <div
            v-if="showApproveButton && !isDeletedList"
          >
            <q-btn
              color="primary"
              :label="$t('changeRequestApproval.approveBtn')"
              @click="openBulkApprovalModal"
            />
          </div>
          <div v-if="hasIntegration && !isParent && !disableSendToIntegration && hasSelectedSessions">
            <q-btn
              icon="file_upload"
              color="primary"
              flat
              round
              dense
              :disable="!canSendSelectedSessionsToIntegration"
              :loading="isSendingSessionNote"
              @click="onTransferSelectedNotes"
            >
              <q-tooltip>
                {{
                  $t('history.buttonSendToIntegration', {
                    integration: getIntegrationName(connectedClinic.integration)
                  })
                }}
              </q-tooltip>
            </q-btn>
          </div>
          <div v-if="!disableExport">
            <q-btn
              icon="ios_share"
              color="primary"
              flat
              round
              dense
              :disable="!hasSelectedSessions"
              menu-self="top middle"
            >
              <q-menu>
                <q-list>
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    data-e2e="button-export-excel"
                    @click="onExportToExcel"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="fas fa-file-excel"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section
                      class="text-no-wrap"
                    >
                      {{ $t('history.export.excel') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    data-e2e="button-export-selected-pdf"
                    @click="onExportSelectedToPDF(false)"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="fas fa-file-pdf"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section
                      class="text-no-wrap"
                    >
                      {{ $t('history.export.pdf') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="!disableExportWithData"
                    v-ripple
                    v-close-popup
                    clickable
                    :disable="isUnableToExportWithData"
                    data-e2e="button-export-pdf-with-data"
                    @click="onExportSelectedToPDF(true)"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="fas fa-file-pdf"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section
                      class="text-no-wrap"
                    >
                      {{ $t('history.export.pdfWithData') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
          <table-views
            v-if="!disableViews"
            :context="localStorageProp+'Columns'"
            :filters="filters"
            :pagination="pagination"
            :columns="selectedColumns"
            :filter-callbacks="columnsWithFilterCallback"
            @load-view="onLoadView"
          />
          <div
            v-if="
              userCanMatchToAppointments
                || userCanRestoreSessionActivities
                || canViewAuditorBehavior
            "
          >
            <q-btn
              icon="more_vert"
              flat
              round
              dense
              @click.stop
            >
              <q-menu>
                <q-list style="min-width: 100px">
                  <div v-if="canViewAuditorBehavior">
                    <div class="full-width">
                      <q-item
                        v-ripple
                        v-close-popup
                        clickable
                        :disable="!canAuditSelected || !canManageAuditorBehavior"
                        @click="onOpenAIAuditDialog"
                      >
                        <q-item-section avatar>
                          <q-icon
                            name="fact_check"
                            color="dark"
                          />
                        </q-item-section>
                        <q-item-section class="text-no-wrap">
                          {{ $t('sessionActivityReports.aiAuditAgent') }}
                        </q-item-section>
                      </q-item>
                      <hr-tooltip v-if="!hasAuditorEntitlement">
                        {{ isStaffBypass
                          ? $t('noteAuditor.tooltipStaffBypass')
                          : $t('noteAuditor.tooltipSubscriptionUnavailable') }}
                      </hr-tooltip>
                      <hr-tooltip v-else-if="!canAuditSelected">
                        {{ $t('sessionActivityReports.aiAuditDisabledTooltip') }}
                      </hr-tooltip>
                    </div>
                  </div>
                  <q-item
                    v-if="userCanMatchToAppointments"
                    v-ripple
                    v-close-popup
                    clickable
                    :disable="!hasReconciliationEligibleActivities"
                    @click="onOpenMatchToAppointments"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="assignment_return"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessionActivityReports.matchToAppointments') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="userCanRestoreSessionActivities"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onOpenRecoverActivitiesDialog"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="restore_from_trash"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessionActivityReports.restoreDeletedActivities') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </div>
    </template>
    <template #body-selection="props">
      <!--
        We should use getOwnPropertyDescriptor in this case to keep selection after table data refreshing
        @see https://quasar.dev/vue-components/table#example--selection-cell-slots-with-range-selection
      -->
      <q-checkbox
        v-if="selection === 'multiple'"
        :model-value="props.selected"
        :disable="isInactive(props.row)"
        @update:model-value="(val, evt) => Object.getOwnPropertyDescriptor(props, 'selected').set(val, evt)"
      >
        <q-tooltip
          v-if="isInactive(props.row)"
        >
          {{ disabledSelectionTooltip }}
        </q-tooltip>
      </q-checkbox>
      <q-radio
        v-else
        :model-value="singleSelectedRow"
        :val="props.row"
        :disable="isInactive(props.row)"
        @update:model-value="onSelectRow"
      >
        <q-tooltip
          v-if="isInactive(props.row)"
        >
          {{ disabledSelectionTooltip }}
        </q-tooltip>
      </q-radio>
    </template>
    <template #header-cell-actions="props">
      <q-th :props="props">
        <q-btn
          flat
          round
          dense
          size="sm"
          color="dark-variant"
          icon="settings"
          @click="onSelectColumns"
        />
      </q-th>
    </template>
    <template #body-cell-sessionName="props">
      <q-td
        :props="props"
        :data-e2e="`row-session-activity-${(props.row.sessionName || '').replace(/ /g, '-')}`"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <div class="text-no-wrap">
          <q-icon
            :name="getSessionActivityTypeIcon(props.row)"
            size="sm"
            color="primary"
            class="q-mr-sm"
          >
            <q-tooltip>
              {{ getSessionActivityTypeLabel(props.row) }}
            </q-tooltip>
          </q-icon>
          {{ props.row.sessionName || getSessionActivityTypeLabel(props.row) }}
        </div>
      </q-td>
    </template>
    <template #body-cell-type="props">
      <q-td
        :props="props"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <div class="text-no-wrap">
          <q-icon
            :name="getSessionActivityTypeIcon(props.row)"
            size="sm"
            color="primary"
            class="q-mr-sm"
          >
            <q-tooltip>
              {{ getSessionActivityTypeLabel(props.row) }}
            </q-tooltip>
          </q-icon>
          {{ getSessionActivityTypeLabel(props.row) }}
        </div>
      </q-td>
    </template>
    <template #body-cell-status="props">
      <q-td
        :props="props"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <span v-if="props.row.status === 'in-progress'">
          <q-spinner-dots
            size="sm"
            color="primary"
          />
          <q-tooltip>
            {{ getSessionActivityStatusLabel(props.row) }}
          </q-tooltip>
        </span>
        <div
          v-else
          class="relative-position inline-block"
        >
          <q-icon
            data-e2e="icon-activity-status"
            :name="getSessionActivityStatusIcon(props.row)"
            size="sm"
            :color="getSessionActivityStatusColor(props.row)"
          />
          <span
            v-if="props.row.hasAddendum"
            class="addendum-badge"
            :class="`addendum-badge--${props.row.addendumStatus === 'pending-signatures' ? 'pending' : 'complete'}`"
          >
            <q-icon
              :name="props.row.addendumStatus === 'pending-signatures' ? 'fas fa-file-signature' : 'task_alt'"
              size="12px"
            />
          </span>
          <q-tooltip>
            {{ getSessionActivityStatusAndAddendumLabel(props.row) }}
          </q-tooltip>
        </div>
      </q-td>
    </template>
    <template #body-cell-aiAuditVerdict="props">
      <q-td
        :props="props"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <q-icon
          v-if="getAuditVerdictIcon(props.row)"
          :name="getAuditVerdictIcon(props.row)"
          size="sm"
          :color="getAuditVerdictColor(props.row)"
        >
          <q-tooltip>
            {{ getAuditVerdictLabel(props.row) }}
          </q-tooltip>
        </q-icon>
      </q-td>
    </template>
    <template #body-cell-aiAuditIsOutdated="props">
      <q-td
        :props="props"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <q-icon
          v-if="getIsOutdated(props.row)"
          name="sync_problem"
          size="sm"
        >
          <q-tooltip>
            {{ $t('history.tableCols.aiAuditor.auditOutdatedTooltip') }}
          </q-tooltip>
        </q-icon>
      </q-td>
    </template>
    <template #body-cell-signaturesUserUrl="props">
      <q-td
        :props="props"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <q-img
          :src="props.value"
        />
      </q-td>
    </template>
    <template
      v-for="userDataField in mergedUserDataFields"
      :key="userDataField.id"
      #[`body-cell-signatures${userDataField.id}Url`]="props"
    >
      <q-td
        :props="props"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <q-img
          :src="props.value"
        />
      </q-td>
    </template>
    <template #body-cell-actions="props">
      <q-td :props="props">
        <q-btn
          v-if="showContextMenu"
          size="sm"
          flat
          round
          icon="more_vert"
          @click.stop
        >
          <q-menu>
            <q-list
              style="min-width: 100px"
              class="text-dark"
            >
              <q-item
                v-if="props.row.session && userHasPrivilege('sessions/editData')"
                v-close-popup
                clickable
                @click="onEdit(props.row)"
              >
                <q-item-section
                  avatar
                  class="text-no-wrap"
                >
                  <q-icon
                    name="edit"
                    color="dark"
                  />
                </q-item-section>
                <q-item-section
                  class="text-no-wrap"
                >
                  {{ $t('sessions.sessionCard.textEditSessionData') }}
                </q-item-section>
              </q-item>
              <q-item
                clickable
              >
                <q-item-section avatar>
                  <q-icon
                    name="ios_share"
                    color="dark"
                  />
                </q-item-section>
                <q-item-section>
                  {{ $t('sessions.sessionCard.textExport') }}
                </q-item-section>
                <q-item-section side>
                  <q-icon
                    name="keyboard_arrow_right"
                    color="dark"
                  />
                </q-item-section>
                <q-menu>
                  <q-item
                    v-if="!disableExportWithData"
                    v-ripple
                    v-close-popup
                    clickable
                    @click="onExportSessionToPDF(props.row, true)"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="fas fa-file-pdf"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section
                      class="text-no-wrap"
                    >
                      {{ $t('sessions.sessionCard.textExportSessionNotesAndDataToPdf') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-ripple
                    v-close-popup
                    clickable
                    data-e2e="button-export-row-pdf"
                    @click="onExportSessionToPDF(props.row, false)"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="fas fa-file-pdf"
                        color="dark"
                      />
                    </q-item-section>
                    <q-item-section
                      class="text-no-wrap"
                    >
                      {{ $t('sessions.sessionCard.textExportSessionNotesToPdf') }}
                    </q-item-section>
                  </q-item>
                </q-menu>
              </q-item>
              <universal-tooltip
                :show-tooltip="disableAuditTrail"
                :text="$t('premiumUpgrade.upgradeHint')"
              >
                <q-item
                  v-if="userCanViewAuditTrail"
                  v-close-popup
                  :disable="disableAuditTrail || offlineMode"
                  clickable
                  @click="onViewAuditTrail(props.row)"
                >
                  <q-item-section
                    avatar
                    class="text-no-wrap"
                  >
                    <q-icon
                      name="fact_check"
                    />
                  </q-item-section>
                  <q-item-section class="text-no-wrap">
                    {{ $t('auditLogReports.viewAuditTrail') }}
                  </q-item-section>
                </q-item>
              </universal-tooltip>
              <q-item
                v-close-popup
                clickable
                @click="onCopySupportInfo(props.row)"
              >
                <q-item-section
                  avatar
                  class="text-no-wrap"
                >
                  <q-icon
                    name="content_copy"
                    color="dark"
                  />
                </q-item-section>
                <q-item-section
                  class="text-no-wrap"
                >
                  {{ $t('common.copySupportInfo') }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </q-td>
    </template>
    <template #body-cell="props">
      <q-td
        :props="props"
        :class="{
          'cursor-pointer': !isInactive(props.row) && !disableCursorPointer,
          'cursor-not-allowed': isInactive(props.row),
        }"
        @click="onClick(props.row)"
      >
        <q-skeleton
          v-if="isLoadingAppointmentSourceCell(props.col, props.row)"
          type="text"
          width="60%"
        />
        <template v-else>
          {{
            isChangeRequestReasonsTooLong(props)
              ? props.value.slice(0, 20) + "..."
              : props.value
          }}
        </template>
        <q-tooltip
          v-if="isChangeRequestReasonsTooLong(props)"
          max-width="240px"
        >
          {{ props.value }}
        </q-tooltip>
      </q-td>
    </template>
  </q-table>
</template>
<script>
import { filterPropertyTypes } from 'boot/globals';
import ViewAuditTrailDialog from 'components/Audit/ViewAuditTrailDialog';
import ChangeRequestConfirmationModal from 'components/ChangeRequest/ChangeRequestConfirmationModal';
import BulkTransferSessionActivities from 'components/SessionActivities/Modals/BulkTransferSessionActivities';
import RecoverSessionActivitiesDialog from 'components/SessionActivities/Modals/RecoverSessionActivitiesDialog.vue';
import TableViews from 'components/UI/TableViews';
import UniversalColumnSelector from 'components/UI/UniversalColumnSelector';
import FilterChipsBar from 'components/UI/FilterChipsBar.vue';
import { withPersistentFlags } from 'components/UI/filterDialog/mandatoryFilters';
import createFilterDialogOpener from 'components/UI/filterDialog/openFilterDialog';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { Timestamp } from 'firebase/firestore';
import {
  chain, concat, filter, find, findIndex, flatMap, get, has, head, includes, isArray, isEmpty, isFunction,
  isNaN, isNumber, isObject, map, reject, size, xor, capitalize,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useSessionActivityChangeRequest } from 'src/composables/sessionActivityChangeRequest';
import { useTime } from 'src/composables/time';
import { canSendStaffActivitiesWithoutLinkedAppointments } from 'src/config/integrations';
import { ADDENDUM_STATUSES } from 'src/constants/sessionActivities';
import { ActivityReconciliationDialog } from 'src/domains/activityReconciliation';
import {
  isSessionActivityAuditable,
  isStoredAuditOutdated,
  NoteAuditDialog,
  useAuditorAccess,
  VERDICTS,
} from 'src/domains/noteAuditor';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionNoteFieldsDB from 'src/fb/sessionNoteFields-db';
import SessionNoteTemplatesDB from 'src/fb/sessionNoteTemplates-db';
import SessionsDB from 'src/fb/sessions-db';
import Auth from 'src/mixins/Auth';
import ClientsMixin from 'src/mixins/ClientsMixin';
import Countries from 'src/mixins/Countries';
import Credentialing from 'src/mixins/Credentialing';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionExportMixin from 'src/mixins/SessionExportMixin';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { network } from 'src/store/network';
import { units } from 'src/store/units';
import { users } from 'src/store/users';
import { copySupportInfoToClipboard, sortByArray } from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';
import getNoteAppointmentFieldPresets from 'src/utils/sessionNoteAppointmentFields';
import { CATEGORY_IDENTIFIERS } from 'src/constants/dynamicFields';
import * as XLSX from 'xlsx';
import { HrTooltip } from 'src/design-system';

// Must match the entry in columnGroups(); a mismatch drops the columns from the picker silently
const INTEGRATION_COLUMN_GROUP = 'integration';
const APPOINTMENT_SOURCE_COLUMN_PREFIX = 'appointmentSourceFields.';

export default {
  name: 'SessionActivitiesTable',

  components: {
    FilterChipsBar,
    HrTooltip,
    TableViews,
    UniversalTooltip,
  },

  mixins: [
    Auth, ClientsMixin, Countries, Credentialing, FiltersMixin, Integrations, Interactions,
    SessionActivitiesMixin, SessionExportMixin, SessionNotesTemplates,
  ],

  props: {
    useFixedSessionActivities: {
      type: Boolean,
      default: false,
    },

    fixedSessionActivities: {
      type: Array,
      default: () => ([]),
    },

    allPrograms: {
      type: Array,
      default: () => ([]),
    },

    requireUserClientFilter: {
      type: Boolean,
      default: false,
    },

    defaultColumns: {
      type: Array,
      default: () => (['status', 'userName', 'type', 'startTimestamp', 'duration']),
    },

    fixedFilters: {
      type: Object,
      default: () => ({}),
    },

    defaultFilters: {
      type: Object,
      default: () => ({}),
    },

    loading: {
      type: Boolean,
      default: false,
    },

    hidePaginationLabel: {
      type: Boolean,
      default: false,
    },

    hideBulkActions: {
      type: Boolean,
      default: false,
    },

    hideExportAll: {
      type: Boolean,
      default: false,
    },

    showContextMenu: {
      type: Boolean,
      default: false,
    },

    localStorageProp: {
      type: String,
      default: null,
    },

    disableViews: {
      type: Boolean,
      default: false,
    },

    disableExport: {
      type: Boolean,
      default: false,
    },

    disableExportWithData: {
      type: Boolean,
      default: false,
    },

    selection: {
      type: String,
      default: 'multiple',
    },

    selectAll: {
      type: Boolean,
      default: false,
    },

    defaultSelection: {
      type: Array,
      default: () => ([]),
    },

    virtualScroll: {
      type: Boolean,
      default: false,
    },

    disableCheck: {
      type: Function,
      default: null,
    },

    disabledSelectionTooltip: {
      type: String,
      default: '',
    },

    disableSendToIntegration: {
      type: Boolean,
      default: false,
    },

    selected: {
      type: Array,
      default: () => ([]),
    },

    rowSelect: {
      type: Boolean,
      default: false,
    },

    rowsPerPageOptions: {
      type: Array,
      default: () => ([25, 50, 100, 500, 1000, 5000]),
    },

    noDataLabel: {
      type: String,
      default: null,
    },

    bordered: {
      type: Boolean,
      default: false,
    },

    isOnMyActivitiesPage: {
      type: Boolean,
      default: false,
    },

    showRestoreSessionActivities: {
      type: Boolean,
      default: false,
    },

    showMatchToAppointments: {
      type: Boolean,
      default: false,
    },

    disableCursorPointer: {
      type: Boolean,
      default: false,
    },

    isDeletedList: {
      type: Boolean,
      default: false,
    },

    failedActivityIds: {
      type: Array,
      default: () => ([]),
    },
  },

  emits: ['update:selected', 'update:row-count', 'click', 'edit', 'change-request-bulk-approve'],

  setup() {
    const { mapChangeRequestData, getFieldDataForApproval } = useSessionActivityChangeRequest();
    const { getLocaleTimeString, getLocaleDateString, getLocalToTimezoneOffsetDate } = useTime();
    const {
      canViewAuditorOutput,
      canViewAuditorBehavior,
      canManageAuditorBehavior,
      hasAuditorEntitlement,
      isStaffBypass,
    } = useAuditorAccess();

    return {
      mapChangeRequestData,
      getFieldDataForApproval,
      getLocaleTimeString,
      getLocaleDateString,
      getLocalToTimezoneOffsetDate,
      canViewAuditorOutput,
      canViewAuditorBehavior,
      canManageAuditorBehavior,
      hasAuditorEntitlement,
      isStaffBypass,
    };
  },

  data() {
    return {
      isLoadingNewSessions: false,
      isSendingSessionNote: false,
      cancelLoadingSessionActivities: false,

      tableViews: [],

      sessionActivities: [],
      sessionActivitiesPaginationPointer: null,
      navigationActivitiesBySession: {},

      cachedSessionNoteFields: [],
      cachedSessionNoteTemplateFields: [],
      allSessionNoteFieldsCached: false,
      allSessionNoteTemplatesCached: false,
      linkedAppointments: {},
      linkedAppointmentsQueue: Promise.resolve(),
      isLoadingLinkedAppointments: false,

      selectedRows: [],
      selectedColumns: [],
      showApproveButton: false,

      filters: {},

      noteStatusTexts: {
        isCompleted: this.$t('common.buttonYes'),
        isNotCompleted: this.$t('common.buttonNo'),
      },

      pagination: {
        rowsNumber: 25,
        rowsPerPage: 25,
        page: 1,
      },

      validSessionNoteFieldTypes: ['select', 'text', 'number', 'date', 'score'],

      validSessionActivityTypes: ['form'],

      // Store the values of applied filters IDs like a string
      appliedFiltersId: null,
    };
  },

  computed: {
    /*
     * Derived once per column change rather than per render: called from the template it
     * returned a fresh array each time, so the chips bar saw a changed prop on every
     * re-render of the parent.
     */
    filterCriteria() {
      return this.getFilterCriteriaFromColumns(this.columns);
    }, // filterCriteria()

    ...mapState(auth, ['userDetails', 'connectedClinic', 'isManagerOrAdministrator', 'isParent', 'allowImmediateSync',
      'isSupervisor', 'isSubscriptionPremium', 'integrationSettings',
      'hasSupervisorCredentials', 'activityTypesSettings', 'userPrivilegesList']),
    ...mapState(units, ['units']),
    ...mapState(users, ['users']),
    ...mapState(network, ['offlineMode']),
    ...mapState(clients, ['clients']),
    ...mapState(dataFields, ['clientDataFields', 'staffMemberDataFields']),

    userCanViewAuditTrail() {
      return this.isManagerOrAdministrator || this.userDetails.admin;
    },

    mustFilterByClientOrUser() {
      return this.requireUserClientFilter
        && !this.isManagerOrAdministrator
        && (!('client' in this.filters) && !('userName' in this.filters));
    },

    hasTableViews() {
      return !isEmpty(this.tableViews);
    },

    disableAuditTrail() {
      return !this.isSubscriptionPremium;
    },

    isUnableToExportWithData() {
      return this.selectedRows.length > 100;
    },

    hasSessionActivities() {
      return !isEmpty(this.sessionActivities);
    },

    hasSelectedSessions() {
      return !isEmpty(this.selectedRows);
    },

    hasReconciliationEligibleActivities() {
      return this.selectedRows.some((sessionActivity) => (
        sessionActivity?.type !== 'form'
      ));
    },

    hasIntegration() {
      return (get(this.connectedClinic, 'integration', null) !== null);
    },

    canSendSelectedSessionsToIntegration() {
      return this.selectedRows.some((sessionActivity) => {
        if (
          sessionActivity.status !== 'closed'
          && !(
            this.allowImmediateSync
            && sessionActivity.status === 'pending-signatures'
            && !sessionActivity.renderedTimestamp
          )
        ) {
          return false;
        }

        return !!sessionActivity.appointment || this.canTransferStaffActivityWithoutLinkedAppointment(sessionActivity);
      });
    },

    canAuditSelected() {
      return this.selectedRows.some((sessionActivity) => isSessionActivityAuditable(sessionActivity));
    },

    visibleColumns() {
      return this.columns
        .filter((column) => this.selectedColumns.includes(column.name) || column.persistent)
        .sort(sortByArray({ arrayToSortBy: this.selectedColumns, searchKey: 'name' }));
    },

    singleSelectedRow() {
      return head(this.selectedRows);
    },

    fromTime() {
      const fromDate = chain(this.filters)
        .get(['startTimestamp', 'filterStatements'])
        .head()
        .get('fromDate')
        .value();

      if (fromDate instanceof Timestamp) {
        return fromDate;
      }

      return null;
    }, // fromTime()

    toTime() {
      const toDate = chain(this.filters)
        .get(['startTimestamp', 'filterStatements'])
        .head()
        .get('toDate')
        .value();
      if (toDate instanceof Timestamp) {
        return toDate;
      }

      return null;
    }, // toTime()

    /**
     * Filters restored from local storage or carried by a saved view can target a column this user may not see.
     * @returns {Object} filters this user is allowed to have
     */
    accessibleFilters() {
      return Object.fromEntries(
        Object.entries(this.filters)
          .filter(([filterKey]) => !this.inaccessibleFilterKeys.includes(filterKey)),
      );
    }, // accessibleFilters()

    appliedFilters() {
      return {
        ...this.accessibleFilters,
        ...this.fixedFilters,
      };
    },

    staffMembers() {
      return filter(this.clients, { type: 'staff' });
    }, // staffMembers()

    clientProfiles() {
      return reject(this.clients, { type: 'staff' });
    }, // clientProfiles()

    filteredSessionActivities() {
      const filteredElements = this.getFilteredElementsWithObjects(
        this.sessionActivities,
        this.appliedFilters,
        {
          clients: this.clientProfiles,
          staffMembers: this.staffMembers,
        },
      );

      // To avoid permission errors, we need to filter out activities related to clients
      // that the user does not have access to when applying a filter based only on the user.
      if (this.requireUserClientFilter && !this.isManagerOrAdministrator && !('client' in this.filters)) {
        const availableClientIds = new Set(this.clients.map((client) => client.id));

        return filteredElements.filter(({ client }) => {
          return client ? availableClientIds.has(client) : true;
        });
      }

      return filteredElements;
    }, // filteredSessionActivities()

    // After filtering, duplicate activities can occasionally occur.
    // Therefore, we use a Map to create an array of unique session activities
    uniqueFilteredSessionActivities() {
      return Array.from(
        new Map(this.filteredSessionActivities.map((activity) => [activity.id, activity])).values(),
      );
    }, // uniqueFilteredSessionActivities()

    paginatedSessionActivities() {
      return this.uniqueFilteredSessionActivities.slice(
        (this.pagination.page - 1) * this.pagination.rowsPerPage,
        this.pagination.page * this.pagination.rowsPerPage,
      );
    },

    mergedUserDataFields() {
      return [
        ...this.clientDataFields.filter(({ type }) => type === 'user'),
        ...this.staffMemberDataFields.filter(({ type }) => type === 'user'),
      ];
    },

    aiAuditorColumns() {
      return [
        {
          name: 'aiAuditVerdict',
          group: 'aiAuditor',
          label: this.$t('history.tableCols.aiAuditor.verdict'),
          align: 'center',
          field: 'audit',
          exportFormatExcel: (audit, sessionActivity) => this.getAuditVerdictLabel(sessionActivity),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterProperty: 'audit.verdict',
          filterPropertyCallback: (row) => row.audit?.verdict ?? 'empty',
          filterItems: [
            ...Object.values(VERDICTS).map((verdict) => ({
              value: verdict,
              label: this.$t(`noteAuditor.verdictOptions.${verdict}`),
            })),
            { value: 'empty', label: this.$t('history.tableCols.aiAuditor.items.empty') },
          ],
        },
        {
          name: 'aiAuditTimestamp',
          group: 'aiAuditor',
          label: this.$t('history.tableCols.aiAuditor.lastAudit'),
          align: 'left',
          field: 'auditMeta',
          format: (auditMeta, sessionActivity) => (this.getLastAuditTimestamp(sessionActivity)
            ? this.getLocaleTimeString(this.getLastAuditTimestamp(sessionActivity), {
              options: { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' },
            })
            : ''
          ),
          exportFormatExcel: (auditMeta, sessionActivity) => (this.getLastAuditTimestamp(sessionActivity)
            ? this.getLocalToTimezoneOffsetDate(this.getLastAuditTimestamp(sessionActivity))
            : ''),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.Date,
          filterProperty: 'auditMeta.lastAuditTimestamp',
          filterPropertyCallback: (row) => this.getLastAuditTimestamp(row),
        },
        {
          name: 'aiAuditIsAudited',
          group: 'aiAuditor',
          label: this.$t('history.tableCols.aiAuditor.audited'),
          align: 'center',
          field: 'auditMeta',
          format: (auditMeta, sessionActivity) => (this.getLastAuditTimestamp(sessionActivity)
            ? this.$t('common.buttonYes')
            : this.$t('common.buttonNo')),
          sortable: false,

          filterable: true,
          filterProperty: 'auditMeta.lastAuditTimestamp',
          filterPropertyCallback: (row) => this.getLastAuditTimestamp(row),
          filterType: filterPropertyTypes.NullValue,
          filterItems: [
            { value: true, label: this.$t('history.tableCols.aiAuditor.notAudited') },
            { value: false, label: this.$t('history.tableCols.aiAuditor.audited') },
          ],
        },
        {
          name: 'aiAuditIsOutdated',
          group: 'aiAuditor',
          label: this.$t('history.tableCols.aiAuditor.auditOutdated'),
          align: 'center',
          field: 'auditMeta',
          format: (auditMeta, sessionActivity) => (this.getIsOutdated(sessionActivity)
            ? this.$t('common.buttonYes')
            : this.$t('common.buttonNo')),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.NullValue,
          filterPropertyCallback: (row) => (this.getIsOutdated(row) ? true : null),
          filterItems: [
            { value: true, label: this.$t('history.tableCols.aiAuditor.notOutdated') },
            { value: false, label: this.$t('history.tableCols.aiAuditor.outdated') },
          ],
        },
      ];
    }, // aiAuditorColumns()

    /** @return {Object[]} appointment note fields this clinic can capture */
    noteAppointmentFields() {
      return getNoteAppointmentFieldPresets(this.sessionNoteFieldPresets);
    }, // noteAppointmentFields()

    linkedAppointmentIds() {
      return [...new Set(this.sessionActivities.map(({ appointment }) => appointment).filter(Boolean))];
    }, // linkedAppointmentIds()

    hasAppointmentSourceFilter() {
      return Object.keys(this.appliedFilters).some((key) => key.startsWith(APPOINTMENT_SOURCE_COLUMN_PREFIX));
    }, // hasAppointmentSourceFilter()

    /**
     * Current appointment values for each activity, keyed by activity id.
     *
     * Built with `compileSessionNoteInformation`, the same function that filled the note, so both
     * columns are built the same way. Reading the appointment fields directly gives different
     * values for the same data, which the table would show as a mismatch.
     *
     * @return {Object<string, Object>}
     */
    appointmentSourceValues() {
      return this.sessionActivities.reduce((values, sessionActivity) => {
        const appointment = this.linkedAppointments[sessionActivity.appointment];
        // Not every delete path unlinks the activity, so it can still point at a deleted appointment
        if (!appointment || (appointment.status === 'deleted')) {
          return values;
        }

        const client = this.clientProfiles.find(({ id }) => id === sessionActivity.client) || null;
        values[sessionActivity.id] = this.compileSessionNoteInformation(
          null,
          client,
          sessionActivity,
          appointment,
        ).appointment;

        return values;
      }, {});
    }, // appointmentSourceValues()

    /**
     * Appointment values as they were frozen into the session note when it was saved, so they can be
     * compared against the appointment source columns in the `integration` group.
     * @return {Object[]}
     */
    sessionNoteAppointmentColumns() {
      const readValue = (sessionNotesFieldData, fieldId) => (
        get(sessionNotesFieldData, [CATEGORY_IDENTIFIERS.APPOINTMENT, fieldId], '')
      );

      return this.noteAppointmentFields
        .map((field) => {
          // These filter on the text the cell shows: serviceCodeModifiers is an array with no options
          // to choose from, and the app has no time-of-day filter
          const filtersOnDisplayedText = ['select', 'time'].includes(field.type);

          return {
            name: `sessionNoteAppointmentFields.${field.id}`,
            group: 'sessionNoteAppointmentFields',
            align: 'left',
            field: 'sessionNotesFieldData',
            label: this.$t('history.tableCols.sessionNoteAppointmentField', { label: field.label }),
            format: (sessionNotesFieldData) => (
              this.formatSessionNoteField(readValue(sessionNotesFieldData, field.id), field.type, field)
            ),
            exportFormatExcel: (sessionNotesFieldData) => (
              this.formatSessionNoteFieldExcel(readValue(sessionNotesFieldData, field.id), field.type)
            ),
            sortable: false,

            filterable: true,
            filterType: filtersOnDisplayedText
              ? filterPropertyTypes.StringMatch
              : this.getFilterTypeForSessionNoteField(field.type),
            ...(filtersOnDisplayedText
              ? {
                filterPropertyCallback: (row) => (
                  this.formatSessionNoteField(readValue(row.sessionNotesFieldData, field.id), field.type, field)
                ),
              }
              : {
                filterProperty: `sessionNotesFieldData.${CATEGORY_IDENTIFIERS.APPOINTMENT}.${field.id}`,
              }),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }, // sessionNoteAppointmentColumns()

    /**
     * The same appointment fields as {@link sessionNoteAppointmentColumns}, valued from the linked
     * appointment instead of the note, so the two can be compared side by side.
     * @return {Object[]}
     */
    appointmentSourceColumns() {
      const integrationId = this.connectedClinic?.integration;
      const integration = this.getIntegrationName(integrationId);
      // compileSessionNoteInformation yields Date objects, where the note-side columns read
      // Timestamps back out of Firestore — hence the separate formatters below.
      const readValue = (row, fieldId) => get(this.appointmentSourceValues, [row.id, fieldId], null);

      return this.noteAppointmentFields
        .map((field) => ({
          name: `${APPOINTMENT_SOURCE_COLUMN_PREFIX}${field.id}`,
          group: INTEGRATION_COLUMN_GROUP,
          align: 'left',
          field: 'appointment',
          label: integrationId
            ? this.$t('history.tableCols.appointmentSourceField', { label: field.label, integration })
            : this.$t('history.tableCols.nativeAppointmentSourceField', { label: field.label }),
          format: (appointment, row) => this.formatAppointmentSourceField(readValue(row, field.id), field.type),
          exportFormatExcel: (appointment, row) => (
            this.formatAppointmentSourceFieldExcel(readValue(row, field.id), field.type)
          ),
          sortable: false,

          filterable: true,
          // The value lives on the linked appointment, not on the row, so no property path reaches it
          filterType: field.type === 'date' ? filterPropertyTypes.Date : filterPropertyTypes.StringMatch,
          filterPropertyCallback: (row) => {
            const value = readValue(row, field.id);
            if (field.type === 'date') {
              // The Date filter compares Timestamps, which is what the note-side values already are
              return value instanceof Date ? Timestamp.fromDate(value) : null;
            }
            return this.formatAppointmentSourceField(value, field.type);
          },
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, // appointmentSourceColumns()

    columns() {
      const cols = [
        {
          name: 'status',
          field: 'status',
          group: null,
          exportLabel: this.$t('history.tableCols.exportStatus'),
          align: 'center',
          exportFormatExcel: (sessionActivityStatus, sessionActivity) => (
            this.getSessionActivityStatusLabel(sessionActivity)
          ),
          sortable: false,

          filterable: !has(this.fixedFilters, 'status'),
          filterType: filterPropertyTypes.StringInArray,
          filterItems: chain(this.sessionActivityStatuses)
            .omitBy({ hidden: true })
            .map((statusInfo, status) => ({ ...statusInfo, value: status }))
            .value(),
        },
        {
          name: 'client',
          group: null,
          label: this.$t('history.tableCols.client'),
          align: 'left',
          field: 'client',
          format: (clientId) => (chain(this.clients).find({ id: clientId }).get('clientName', '').value()),
          sortable: false,

          filterable: !has(this.fixedFilters, 'client'),
          filterType: filterPropertyTypes.Client,
        },
        {
          name: 'userName',
          group: null,
          label: this.$t('history.tableCols.user'),
          align: 'left',
          field: 'userName',
          sortable: false,

          filterable: !has(this.fixedFilters, 'userName') && !this.isParent,
          filterProperty: 'user',
          filterType: filterPropertyTypes.User,
        },
        {
          name: 'userUnits',
          group: null,
          label: this.$t('history.tableCols.userUnits'),
          align: 'left',
          field: 'user',
          format: (userId) => (
            chain(this.units)
              .filter((unit) => chain(this.users)
                .find({ userId })
                .get('units')
                .includes(unit.id)
                .value())
              .map('unitName', '')
              .join(' | ')
              .value()
          ),
          sortable: false,
        },
        ...(this.localStorageProp !== 'ownActivities' ? [{
          name: 'userRole',
          group: null,
          label: this.$t('history.tableCols.userRole'),
          align: 'left',
          field: 'user',
          format: (user) => {
            const { userPrivilege, role } = this.usersObject[user] || {};
            return this.privilegesObject[userPrivilege || role]?.label || '';
          },
          sortable: false,
          filterable: !has(this.fixedFilters, 'userRole') && !this.isParent,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: this.userPrivilegesList,
          filterPropertyCallback: (row) => {
            const { userPrivilege, role } = this.usersObject[row.user] || {};
            return userPrivilege || role || '';
          },
        }] : []),
        {
          name: 'startTimestamp',
          group: null,
          required: true,
          label: this.$t('history.tableCols.startTimestamp'),
          align: 'left',
          field: 'startTimestamp',
          format: (startTimestamp) => (startTimestamp instanceof Timestamp
            ? this.getLocaleTimeString(startTimestamp, {
              options: { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' },
            }) : ''
          ),
          exportFormatExcel: (startTimestamp) => (startTimestamp instanceof Timestamp
            ? this.getLocalToTimezoneOffsetDate(startTimestamp)
            : ''),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.Date,
          filterPrefix: null,
          filterOrder: 1,
        },
        {
          name: 'startDate',
          group: null,
          label: this.$t('history.tableCols.startDate'),
          align: 'left',
          field: 'startTimestamp',
          format: (startTimestamp) => (startTimestamp instanceof Timestamp
            ? this.getLocaleDateString(startTimestamp, {
              options: { year: 'numeric', month: 'numeric', day: 'numeric' },
            }) : ''
          ),
          exportFormatExcel: (startTimestamp) => (startTimestamp instanceof Timestamp
            ? this.getLocalToTimezoneOffsetDate(startTimestamp)
            : ''),
          sortable: false,
        },
        {
          name: 'startTime',
          group: null,
          label: this.$t('history.tableCols.startTime'),
          align: 'left',
          field: 'startTimestamp',
          format: (startTimestamp) => (startTimestamp instanceof Timestamp
            ? this.getLocaleTimeString(startTimestamp, { options: { hour: '2-digit', minute: '2-digit' } }) : ''
          ),
          exportFormatExcel: (startTimestamp) => (startTimestamp instanceof Timestamp
            ? this.getLocalToTimezoneOffsetDate(startTimestamp)
            : ''),
          sortable: false,
        },
        {
          name: 'endTimestamp',
          group: null,
          label: this.$t('history.tableCols.endTimestamp'),
          align: 'left',
          field: 'endTimestamp',
          format: (endTimestamp) => (endTimestamp instanceof Timestamp
            ? this.getLocaleTimeString(endTimestamp, { options: { hour: '2-digit', minute: '2-digit' } }) : ''
          ),
          exportFormatExcel: (endTimestamp) => (endTimestamp instanceof Timestamp
            ? this.getLocalToTimezoneOffsetDate(endTimestamp)
            : ''),
          sortable: false,
        },
        {
          name: 'duration',
          field: 'duration',
          group: null,
          label: this.$t('history.tableCols.duration'),
          align: 'left',
          format: (duration, sessionActivity) => this.getSessionActivityDuration(sessionActivity),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.Duration,
          filterDependency: {
            value: 'startTimestamp',
            isSet: !!this.appliedFilters?.startTimestamp,
            bannerMessage: this.$t('history.filter.dateFilterRequired', {
              requestedFilter: this.$t('history.tableCols.duration').toLocaleLowerCase(),
            }),
          },
          filterPropertyCallback: (row) => {
            return this.stringToDuration(this.getSessionActivityDuration(row));
          },
        },
        {
          name: 'signatureTimeDiscrepancy',
          field: 'signatures',
          group: null,
          label: this.$t('history.tableCols.signatureTimeDiscrepancy'),
          align: 'left',
          format: (signatures, sessionActivity) => this.calcSignatureTimeDiscrepancy(sessionActivity),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.Duration,
          filterDependency: {
            value: 'startTimestamp',
            isSet: !!this.appliedFilters?.startTimestamp,
            bannerMessage: this.$t('history.filter.dateFilterRequired', {
              requestedFilter: this.$t('history.tableCols.signatureTimeDiscrepancy').toLocaleLowerCase(),
            }),
          },

          filterPropertyCallback: (row) => {
            const val = this.calcSignatureTimeDiscrepancy(row, true);
            return Number.isFinite(val) ? Math.abs(val) : null;
          },
        },
        {
          name: 'sessionName',
          group: null,
          label: this.$t('history.tableCols.sessionName'),
          align: 'left',
          field: 'sessionName',
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
        },
        ...(this.canViewAuditorOutput ? this.aiAuditorColumns : []),
        {
          name: 'isPendingOwnSignature',
          group: null,
          label: this.$t('history.tableCols.isPendingOwnSignature'),
          align: 'left',
          field: 'requiredSignatories',
          sortable: false,
          format: (requiredSignatories) => (
            !chain(requiredSignatories)
              .reject({ completed: true })
              .filter({ userId: this.userDetails.userId })
              .isEmpty()
              .value()
              ? this.$t('common.buttonYes')
              : this.$t('common.buttonNo')
          ),
        },
        {
          name: 'isPendingReview',
          group: null,
          label: this.$t('history.tableCols.isPendingReview'),
          align: 'left',
          field: 'status',
          sortable: false,
          format: (status) => (
            status === 'pending-review' ? this.$t('common.buttonYes') : this.$t('common.buttonNo')),
        },
        {
          name: 'startGeolocation',
          group: null,
          label: this.$t('history.tableCols.startGeolocation'),
          align: 'left',
          field: 'startGeolocation',
          format: (startGeolocation) => (
            isObject(startGeolocation)
              ? `${startGeolocation.latitude?.toFixed(4)}, ${startGeolocation.longitude?.toFixed(4)}`
              : ''
          ),
          sortable: false,
        },
        {
          name: 'endGeolocation',
          group: null,
          label: this.$t('history.tableCols.endGeolocation'),
          align: 'left',
          field: 'endGeolocation',
          format: (endGeolocation) => (
            isObject(endGeolocation)
              ? `${endGeolocation.latitude?.toFixed(4)}, ${endGeolocation.longitude?.toFixed(4)}`
              : ''
          ),
          sortable: false,
        },
        {
          name: 'distance',
          group: null,
          label: this.$t('history.tableCols.distance'),
          align: 'left',
          field: 'distance',
          format: (distance) => (this.formatDistance(distance, get(this.connectedClinic, 'country'))),
          exportFormatExcel: (distance) => ((isNumber(distance) && !isNaN(distance)) ? distance : ''),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.Number,
        },
        {
          name: 'type',
          field: 'type',
          group: null,
          label: this.$t('history.tableCols.exportType'),
          align: 'left',
          exportFormatExcel:
            (sessionActivityType, sessionActivity) => this.getSessionActivityTypeLabel(sessionActivity),
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: map(
            this.activityTypesSettings,
            (sessionActivityType, key) => ({ value: key, ...sessionActivityType }),
          ),
        },
        {
          name: 'id',
          group: null,
          label: this.$t('history.tableCols.sessionActivityId'),
          align: 'left',
          field: 'id',
          sortable: false,

          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
        },
        {
          name: 'isLinked',
          group: null,
          label: this.$t('history.tableCols.isLinked'),
          align: 'left',
          field: 'appointment',
          sortable: false,
          format: (appointment) => (appointment ? this.$t('common.buttonYes') : this.$t('common.buttonNo')),

          filterable: true,
          filterProperty: 'appointment',
          filterType: filterPropertyTypes.NullValue,
          filterItems: [
            { value: true, label: this.$t('history.tableCols.isNotLinked') },
            { value: false, label: this.$t('history.tableCols.isLinked') },
          ],
        },
        ...(canSendStaffActivitiesWithoutLinkedAppointments(this.connectedClinic?.integration) ? [
          {
            name: 'appointmentSource',
            group: null,
            label: this.$t('history.tableCols.appointmentSource.label'),
            align: 'left',
            field: 'appointmentSource',
            sortable: false,
            format: (source) => get(this.$tm('history.tableCols.appointmentSource.items'), source, ''),

            filterable: true,
            filterType: filterPropertyTypes.StringInArray,
            filterItems: map(this.$tm('history.tableCols.appointmentSource.items'), (label, source) => ({
              value: source,
              label,
            })),
            filterPropertyCallback: ({ appointmentSource }) => appointmentSource ?? 'empty',
          },
          {
            name: 'allowSendWithoutLinkedAppointments',
            group: null,
            align: 'left',
            label: this.$t('history.tableCols.allowSendWithoutLinkedAppointments'),
            field: 'type',
            sortable: false,
            format: (type) => {
              return this.allowSendWithoutLinkedAppointments(type)
                ? this.$t('common.buttonYes')
                : this.$t('common.buttonNo');
            },

            filterable: true,
            filterType: filterPropertyTypes.StringInArray,
            filterItems: [
              { value: true, label: this.$t('common.buttonYes') },
              { value: false, label: this.$t('common.buttonNo') },
            ],
            filterPropertyCallback: ({ type }) => this.allowSendWithoutLinkedAppointments(type),
          },
        ] : []),
        {
          name: 'hasAddendum',
          group: null,
          label: this.$t('addendums.filters.hasAddendum'),
          align: 'left',
          field: 'hasAddendum',
          sortable: false,
          format: (hasAddendum) => (hasAddendum ? this.$t('common.buttonYes') : this.$t('common.buttonNo')),

          filterable: true,
          filterType: filterPropertyTypes.NullValue,
          // NullValue matches on "is null", so "No" is `true`; legacy activities without the field count as "No"
          filterPropertyCallback: (row) => row.hasAddendum || null,
          filterItems: [
            { value: false, label: this.$t('common.buttonYes') },
            { value: true, label: this.$t('common.buttonNo') },
          ],
        },
        {
          name: 'addendumStatus',
          group: null,
          label: this.$t('addendums.filters.addendumStatus'),
          align: 'left',
          field: 'addendumStatus',
          sortable: false,
          format: (addendumStatus) => (
            addendumStatus ? this.$t(`addendums.statuses.${addendumStatus}`) : ''
          ),

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: map(
            ADDENDUM_STATUSES,
            (addendumStatus) => ({
              value: addendumStatus,
              label: this.$t(`addendums.statuses.${addendumStatus}`),
            }),
          ),
        },
        {
          name: 'requiredSignatories',
          group: 'signatures',
          label: this.$t('history.tableCols.signature.requiredSignatories'),
          align: 'left',
          field: 'requiredSignatories',
          sortable: false,
          isImage: true,
          format: (requiredSignatories) => (chain(requiredSignatories)
            .reject({ completed: true })
            .map((requiredSignature) => (
              chain(this.mergedUserDataFields)
                .find({ id: requiredSignature.clientDataField })
                .get('label')
                .value()
            ))
            .compact()
            .join(' | ')
            .value()
          ),

          filterable: !isEmpty(this.mergedUserDataFields),
          filterType: filterPropertyTypes.ArrayInArray,
          filterItems: chain(this.mergedUserDataFields)
            .sortBy('order')
            .map((field) => ({
              value: field.id,
              label: field.label,
            }))
            .value(),
          filterArrayProperty: 'clientDataField',
        },
        {
          name: 'requiredSignatoriesUserIds',
          group: 'signatures',
          label: this.$t('history.tableCols.signature.requiredSignatoriesUserIds'),
          align: 'left',
          field: 'requiredSignatoriesUserIds',
          sortable: false,
          isImage: true,
          format: (requiredSignatoriesUserIds) => (
            chain(requiredSignatoriesUserIds)
              .map((userId) => chain(this.users).find({ userId }).get('name', '').value())
              .join(' | ')
              .value()
          ),

          filterable: !has(this.fixedFilters, 'requiredSignatoriesUserIds')
            && (this.isManagerOrAdministrator || this.isSupervisor),
          filterType: filterPropertyTypes.ArrayContainsUser,
        },
        {
          name: 'signaturesUserUrl',
          group: 'signatures',
          label: this.$t('history.tableCols.signature.userUrl'),
          align: 'left',
          field: 'signatures',
          sortable: false,
          isImage: true,
          format: (signatures, row) => (
            chain(signatures)
              .reject('clientDataField')
              .find({ userId: row.user })
              .get('url')
              .value()
          ),

          filterable: true,
          filterProperty: 'signatures',
          filterType: filterPropertyTypes.NullValue,
          filterPropertyCallback: (row) => (
            row.signatures
              ?.filter((signature) => !signature?.clientDataField)
              ?.find((signature) => signature?.userId === row.user)
              ?.url
          ),
          filterItems: [
            { value: false, label: this.$t('history.tableCols.signature.hasUserUrl') },
            { value: true, label: this.$t('history.tableCols.signature.hasNoUserUrl') },
          ],
        },
        {
          name: 'signaturesUserTimestamp',
          group: 'signatures',
          label: this.$t('history.tableCols.signature.userTimestamp'),
          align: 'left',
          field: 'signatures',
          sortable: false,
          format: (signatures, row) => {
            const signatureTimestamp = chain(signatures)
              .reject('clientDataField')
              .find({ userId: row.user })
              .get('timestamp')
              .value();
            return (signatureTimestamp instanceof Timestamp
              ? this.getLocaleTimeString(signatureTimestamp, {
                options: { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' },
              }) : ''
            );
          },
          exportFormatExcel: (signatures, row) => {
            const signatureTimestamp = chain(signatures)
              .reject('clientDataField')
              .find({ userId: row.user })
              .get('timestamp')
              .value();
            return (signatureTimestamp instanceof Timestamp
              ? this.getLocalToTimezoneOffsetDate(signatureTimestamp)
              : '');
          },
        },
        {
          name: 'signaturesUserGeolocation',
          group: 'signatures',
          label: this.$t('history.tableCols.signature.userGeolocation'),
          align: 'left',
          field: 'signatures',
          sortable: false,
          format: (signatures, row) => {
            const geolocation = chain(signatures)
              .reject('clientDataField')
              .find({ userId: row.user })
              .get('geolocation')
              .value();
            if (!geolocation) {
              return '';
            }
            return `${geolocation.latitude?.toFixed(4)}, ${geolocation.longitude?.toFixed(4)}`;
          },
        },
        {
          name: 'requestedStartTime',
          group: 'changeRequest',
          label: this.$t('history.tableCols.changeRequest.requestedStartTime'),
          align: 'left',
          field: 'changeRequest',
          sortable: false,
          format: (changeRequest) => {
            if (!changeRequest || changeRequest.status !== 'pending') {
              return '';
            }
            const startKey = changeRequest.fields?.find((field) => field.startTimestamp);

            return (startKey && startKey.startTimestamp instanceof Timestamp)
              ? this.getLocaleTimeString(startKey.startTimestamp, { options: { hour: '2-digit', minute: '2-digit' } })
              : '';
          },
        },
        {
          name: 'requestedEndTime',
          group: 'changeRequest',
          label: this.$t('history.tableCols.changeRequest.requestedEndTime'),
          align: 'left',
          field: 'changeRequest',
          sortable: false,
          format: (changeRequest) => {
            if (!changeRequest || changeRequest.status !== 'pending') {
              return '';
            }
            const endKey = changeRequest.fields?.find((field) => field.endTimestamp);

            return (endKey && endKey.endTimestamp instanceof Timestamp)
              ? this.getLocaleTimeString(endKey.endTimestamp, { options: { hour: '2-digit', minute: '2-digit' } })
              : '';
          },
        },
        {
          name: 'requestReason',
          group: 'changeRequest',
          label: this.$t('history.tableCols.changeRequest.requestReason'),
          align: 'left',
          field: 'changeRequest',
          sortable: false,
          format: (changeRequest) => (changeRequest?.reason || ''),

          filterable: true,
          filterProperty: 'changeRequest.reason',
          filterType: filterPropertyTypes.NullValue,
          filterItems: [
            { value: true, label: this.$t('history.tableCols.changeRequest.noRequestReason') },
            { value: false, label: this.$t('history.tableCols.changeRequest.requestReason') },
          ],
        },
      ];

      if (!this.isParent) {
        cols.push(
          {
            name: 'rejectionReason',
            group: 'changeRequest',
            label: this.$t('history.tableCols.changeRequest.rejectionReason'),
            align: 'left',
            field: 'changeRequest',
            sortable: false,
            format: (changeRequest) => (changeRequest?.rejectionReason || ''),

            filterable: true,
            filterProperty: 'changeRequest.rejectionReason',
            filterType: filterPropertyTypes.NullValue,
            filterItems: [
              { value: true, label: this.$t('history.tableCols.changeRequest.noRejectionReason') },
              { value: false, label: this.$t('history.tableCols.changeRequest.rejectionReason') },
            ],
          },
        );
      }

      cols.push(
        ...flatMap(this.mergedUserDataFields, (field) => ([
          {
            name: `signatures${field.id}Url`,
            group: 'signatures',
            label: this.$t('history.tableCols.signature.clientDataFieldUrl', { clientDataField: field.label }),
            align: 'left',
            field: 'signatures',
            sortable: false,
            isImage: true,
            format: (signatures) => (chain(signatures).find({ clientDataField: field.id }).get('url').value()),

            filterable: true,
            filterProperty: 'signatures',
            filterType: filterPropertyTypes.NullValue,
            filterPropertyCallback: (row) => (
              row.signatures?.find((signature) => signature?.clientDataField === field.id)?.url
            ),
            filterItems: [
              {
                value: false,
                label: this.$t(
                  'history.tableCols.signature.hasClientDataFieldUrl',
                  { clientDataField: field.label },
                ),
              },
              {
                value: true,
                label: this.$t(
                  'history.tableCols.signature.hasNoClientDataFieldUrl',
                  { clientDataField: field.label },
                ),
              },
            ],

          },
          {
            name: `signatures${field.id}Timestamp`,
            group: 'signatures',
            label: this.$t('history.tableCols.signature.clientDataFieldTimestamp', { clientDataField: field.label }),
            align: 'left',
            field: 'signatures',
            sortable: false,
            format: (signatures) => {
              const signatureTimestamp = chain(signatures).find({ clientDataField: field.id }).get('timestamp').value();
              return (signatureTimestamp instanceof Timestamp
                ? this.getLocaleTimeString(signatureTimestamp, {
                  options: { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' },
                }) : ''
              );
            },
            exportFormatExcel: (signatures) => {
              const signatureTimestamp = chain(signatures).find({ clientDataField: field.id }).get('timestamp').value();
              return (signatureTimestamp instanceof Timestamp
                ? this.getLocalToTimezoneOffsetDate(signatureTimestamp)
                : '');
            },
          },
          {
            name: `signatures${field.id}Geolocation`,
            group: 'signatures',
            label: this.$t('history.tableCols.signature.clientDataFieldGeolocation', { clientDataField: field.label }),
            align: 'left',
            field: 'signatures',
            sortable: false,
            format: (signatures) => {
              const geolocation = chain(signatures).find({ clientDataField: field.id }).get('geolocation').value();
              if (!geolocation) {
                return '';
              }
              return `${geolocation.latitude?.toFixed(4)}, ${geolocation.longitude?.toFixed(4)}`;
            },
          },
        ])),
      );
      cols.push(...chain(this.clientColumns)
        .filter((clientColumn) => includes(clientColumn.profileTypes, 'client'))
        .reject({ name: 'clientName' })
        .map((column) => ({
          ...column,
          name: `client.${column.name}`,
          group: 'client',
          format: (fieldName, row) => {
            const client = find(this.clients, { id: row.client });
            if (!isFunction(column.format)) {
              return get(client, column.field, '');
            }
            return column.format(get(client, column.field, ''), client);
          },
          selectionLabel: column.label,
          label: this.$t('history.tableCols.clientPrefix', { label: column.label }),

          filterable: column.filterable && !has(this.fixedFilters, 'client'),
          filterObject: 'clients',
          filterObjectKey: 'client',
          filterObjectForeignKey: 'id',
        }))
        .value());

      cols.push(
        ...map(this.clientDataColumns, (column) => ({
          ...column,
          name: `clientData.${column.name}`,
          group: 'clientData',
          format: (fieldName, row) => {
            const client = find(this.clients, { id: row.client });
            if (!isFunction(column.format)) {
              return get(client, column.field, '');
            }
            return column.format(get(client, column.field, ''), client);
          },

          selectionLabel: column.label,
          label: this.$t('history.tableCols.clientDataPrefix', { label: column.label }),

          filterable: column.filterable && !has(this.fixedFilters, 'client'),
          filterObject: 'clients',
          filterObjectKey: 'client',
          filterObjectForeignKey: 'id',
        })),
      );
      cols.push(
        ...chain(this.clientColumns)
          .filter((column) => includes(column.profileTypes, 'staffMember'))
          .map((column) => ({
            ...column,
            name: `staffMember.${column.name}`,
            group: 'staffMember',
            format: (fieldName, row) => {
              if (!row.user) {
                return '';
              }
              const staffMember = find(this.clients, { user: row.user });
              if (!staffMember) {
                return '';
              }
              if (!isFunction(column.format)) {
                return get(staffMember, column.field, '');
              }
              return column.format(get(staffMember, column.field, ''), staffMember);
            },
            selectionLabel: column.label,
            label: this.$t('history.tableCols.staffMemberPrefix', { label: `${column.label}` }),

            filterable: column.filterable && !has(this.fixedFilters, 'userName'),
            filterObject: 'staffMembers',
            filterObjectKey: 'user',
            filterObjectForeignKey: 'user',
          }))
          .value()
        ,
      );
      cols.push(
        ...map(
          this.staffMemberDataColumns,
          (column) => ({
            ...column,
            name: `staffMemberData.${column.name}`,
            group: 'staffMemberData',
            format: (fieldName, row) => {
              if (!row.user) {
                return '';
              }
              const staffMember = find(this.clients, { user: row.user });
              if (!staffMember || !isFunction(column.format)) {
                return '';
              }
              return column.format(get(staffMember, column.field, ''), staffMember);
            },
            selectionLabel: column.label,
            label: this.$t('history.tableCols.staffMemberDataPrefix', { label: column.label }),

            filterable: column.filterable && !has(this.fixedFilters, 'userName'),
            filterObject: 'staffMembers',
            filterObjectKey: 'user',
            filterObjectForeignKey: 'user',
          }),
        ),
      );
      cols.push(
        ...map(
          this.credentialingStaffMemberColumnsOrdered,
          (column) => ({
            ...column,
            name: `staffCredentials.${column.name}`,
            group: 'staffCredentials',
            format: (fieldName, row) => {
              if (!row.user) {
                return '';
              }
              const staffMember = find(this.clients, { user: row.user });
              if (!staffMember || !isFunction(column.format)) {
                return '';
              }
              return column.format(get(staffMember, column.field, ''), staffMember);
            },
            selectionLabel: column.label,
            label: this.$t('history.tableCols.staffCredentialsPrefix', { label: column.label }),
          }),
        ),
      );

      cols.push(
        ...chain(this.cachedSessionNoteFields)
          .map((field) => ({
            name: `sessionNoteFields.${field.id}`,
            group: 'sessionNoteFields',
            align: 'left',
            field: 'sessionNotesFieldData',
            label: field.label,
            format: (sessionNotesFieldData) => (
              this.formatSessionNoteField(
                get(sessionNotesFieldData, [field.category, field.id], ''),
                field.type,
                field,
              )
            ),
            exportFormatExcel: (sessionNotesFieldData) => (
              this.formatSessionNoteFieldExcel(
                get(sessionNotesFieldData, [field.category, field.id], ''),
                field.type,
              )
            ),
            sortable: false,

            filterable: true,
            filterType: this.getFilterTypeForSessionNoteField(field.type, field.allowMultipleOptions),
            filterProperty: `sessionNotesFieldData.${field.category}.${field.id}`,
            filterItems: map(field.options, (option) => ({
              value: option,
              label: option,
            })),
          }))
          .sortBy('label')
          .value(),
      );

      cols.push(...this.sessionNoteAppointmentColumns);

      cols.push(
        ...chain(this.cachedSessionNoteTemplateFields)
          .filter((templateField) => templateField.sessionActivityTypes.includes('form'))
          .map((templateField) => ({
            name: `sessionNoteTemplateFields.${templateField.id}`,
            group: `formData.${templateField.templateId}`,
            align: 'left',
            field: 'sessionNotesFieldData',
            label: templateField.field.label,
            format: (sessionNotesTemplateFieldData) => (
              this.formatSessionNoteField(
                get(
                  sessionNotesTemplateFieldData,
                  templateField.field.category
                    ? [templateField.field.category, templateField.field.id]
                    : [templateField.field.id],
                  '',
                ),
                templateField.field.type,
                templateField.field,
              )
            ),
            exportFormatExcel: (sessionNotesTemplateFieldData) => (
              this.formatSessionNoteFieldExcel(
                get(
                  sessionNotesTemplateFieldData,
                  templateField.field.category
                    ? [templateField.field.category, templateField.field.id]
                    : [templateField.field.id],
                  '',
                ),
                templateField.field.type,
              )
            ),
            sortable: false,

            filterable: false,
          }))
          .sortBy('label')
          .value(),
      );

      if (get(this.connectedClinic, 'integration')) {
        const { allowScheduling } = this.integrationSettings;
        const integration = this.getIntegrationName(this.connectedClinic.integration);
        const referenceLabel = (labelKey) => (allowScheduling
          ? this.$t(labelKey)
          : this.$t('history.tableCols.appointmentSourceField', { label: this.$t(labelKey), integration }));

        cols.push(...[
          {
            name: 'integrationReference',
            group: allowScheduling ? null : 'integration',
            label: referenceLabel('history.tableCols.appointmentReferenceId'),
            align: 'left',
            field: 'appointmentData',
            format: (appointmentData) => (appointmentData ? get(appointmentData, 'integrationReference', '') : ''),
            sortable: false,
            requireIntegration: true,

            filterable: true,
            filterType: filterPropertyTypes.StringMatch,
            filterProperty: 'appointmentData.integrationReference',
          },
          {
            name: 'integrationClientRef',
            group: allowScheduling ? 'integrationInfo' : 'integration',
            label: referenceLabel('history.tableCols.clientReferenceId'),
            align: 'left',
            field: 'appointmentData',
            format: (appointmentData, row) => (
              appointmentData?.integrationClientRef
              || (this.connectedClinic.integration === 'aloha' && appointmentData?.integrationClientParRef)
              || (this.clients.find((client) => client.id === row.client)?.integrationReference)
              || ''
            ),
            sortable: false,
            requireIntegration: true,

            filterable: true,
            filterType: filterPropertyTypes.StringMatch,
            filterPropertyCallback: (row) => (
              row.appointmentData?.integrationClientRef
              || (this.connectedClinic.integration === 'aloha' && row.appointmentData?.integrationClientParRef)
              || (this.clients.find((client) => client.id === row.client)?.integrationReference)
              || ''
            ),
          },
          {
            name: 'integrationStaffRef',
            group: allowScheduling ? 'integrationInfo' : 'integration',
            label: referenceLabel('history.tableCols.staffReferenceId'),
            align: 'left',
            field: 'appointmentData',
            format: (appointmentData, row) => (
              appointmentData?.integrationStaffRef
              || (this.connectedClinic.integration === 'aloha' && appointmentData?.integrationStaffParRef)
              || (this.users.find((user) => user.id === row.user)?.integrationReference)
              || ''
            ),
            sortable: false,
            requireIntegration: true,

            filterable: true,
            filterType: filterPropertyTypes.StringMatch,
            filterPropertyCallback: (row) => (
              row.appointmentData?.integrationStaffRef
              || (this.connectedClinic.integration === 'aloha' && row.appointmentData?.integrationStaffParRef)
              || (this.users.find((user) => user.id === row.user)?.integrationReference)
              || ''
            ),
          },
          {
            name: 'integrationAppointmentType',
            group: allowScheduling ? null : 'integration',
            label: referenceLabel('history.tableCols.appointmentReferenceType'),
            align: 'left',
            field: 'appointmentData',
            format: (appointmentData) => get(appointmentData, 'integrationAppointmentType', ''),
            sortable: false,
            requireIntegration: true,

            filterable: true,
            filterType: filterPropertyTypes.StringMatch,
            filterProperty: 'appointmentData.integrationAppointmentType',
          },
        ]);
      }

      if (this.integrationSettings.allowScheduling) {
        cols.push({
          name: 'integrationItemId',
          group: 'integrationInfo',
          label: this.$t('history.tableCols.integrationItemId', {
            integration: capitalize(this.connectedClinic.integration),
          }),
          align: 'left',
          field: 'appointmentData',
          format: (appointmentData) => get(appointmentData, 'integrationReference', ''),
          sortable: false,
          filterable: true,
          filterType: filterPropertyTypes.StringMatch,
          filterProperty: 'appointmentData.integrationReference',
        });
      }

      cols.push(...this.appointmentSourceColumns);

      cols.push(...[
        {
          name: 'actions',
          label: null,
          field: '',
          align: 'right',
          sortable: false,
          hideFromSelection: true,
          persistent: true,
          preventExport: true,
        },
      ]);
      return cols;
    }, // columns()

    inaccessibleFilterKeys() {
      return this.canViewAuditorOutput
        ? []
        : this.aiAuditorColumns.map(({ name }) => name);
    }, // inaccessibleFilterKeys()

    formDataColumnGroup() {
      const uniqueTemplatesMap = new Map();
      this.cachedSessionNoteTemplateFields.forEach((field) => {
        if (!uniqueTemplatesMap.has(field.templateId)) {
          uniqueTemplatesMap.set(field.templateId, {
            group: `formData.${field.templateId}`,
            label: field.templateName || '',
          });
        }
      });
      const uniqueTemplates = Array.from(uniqueTemplatesMap.values()).sort((a, b) => a.label.localeCompare(b.label));

      return {
        group: 'formData',
        label: this.$t('history.tableGroups.formDataFields'),
        subGroups: uniqueTemplates,
      };
    }, // formDataColumnGroups()

    columnGroups() {
      return [
        ...(this.canViewAuditorOutput ? [
          {
            group: 'aiAuditor',
            label: this.$t('history.tableGroups.aiAuditor'),
          },
        ] : []),
        {
          group: 'signatures',
          label: this.$t('history.tableGroups.signatures'),
        },
        {
          group: 'client',
          label: this.$t('history.tableGroups.client'),
        },
        {
          group: 'clientData',
          label: this.$t('history.tableGroups.clientData'),
        },
        {
          group: 'staffMember',
          label: this.$t('history.tableGroups.staffMember'),
        },
        {
          group: 'staffMemberData',
          label: this.$t('settings.users.columnGroupStaffMemberData'),
        },
        {
          group: 'staffCredentials',
          label: this.$t('history.tableGroups.staffCredentials'),
        },
        {
          group: 'sessionNoteFields',
          label: this.$t('history.tableGroups.sessionNoteFields'),
        },
        {
          group: 'sessionNoteAppointmentFields',
          label: this.$t('history.tableGroups.sessionNoteAppointmentFields'),
        },
        this.formDataColumnGroup,
        {
          group: INTEGRATION_COLUMN_GROUP,
          label: this.$t('history.tableGroups.integration', {
            integration: this.getIntegrationName(get(this.connectedClinic, 'integration')),
          }),
        },
        ...(this.integrationSettings.allowScheduling ? [{
          group: 'integrationInfo',
          label: this.$t('history.tableGroups.integrationInfo', {
            integration: capitalize(this.connectedClinic.integration),
          }),
        }] : []),
        {
          group: 'changeRequest',
          label: this.$t('history.tableGroups.changeRequest'),
        },
      ];
    }, // columnGroups()

    selectedSessionNoteFields() {
      return chain(this.selectedColumns)
        .filter((selectedColumn) => selectedColumn.startsWith('sessionNoteFields.'))
        .map((selectedColumn) => selectedColumn.replace('sessionNoteFields.', ''))
        .value();
    }, // selectedSessionNoteFields()

    selectedSessionNoteTemplateFields() {
      return chain(this.selectedColumns)
        .filter((selectedColumn) => selectedColumn.startsWith('sessionNoteTemplateFields.'))
        .map((selectedColumn) => selectedColumn.replace('sessionNoteTemplateFields.', ''))
        .value();
    }, // selectedSessionNoteTemplateFields()

    userCanRestoreSessionActivities() {
      return this.isManagerOrAdministrator && this.showRestoreSessionActivities;
    }, // userCanRestorePrograms()

    userCanMatchToAppointments() {
      return this.isManagerOrAdministrator && this.showMatchToAppointments;
    }, // userCanMatchToAppointments()

    privilegesObject() {
      return Object.fromEntries(this.userPrivilegesList.map((privilege) => [privilege.value, privilege]));
    },

    usersObject() {
      return Object.fromEntries(this.users.map((user) => [user.userId, user]));
    },

    columnsWithFilterCallback() {
      return this.columns.filter(({ filterPropertyCallback }) => filterPropertyCallback);
    },

  },

  watch: {
    sessionActivities: {
      deep: false,
      immediate: true,
      handler() {
        // Replace selected items with their latest versions from paginatedSessionActivities,
        // removing any that no longer exist.
        const selected = this.selected
          .map((selectedItem) => find(this.paginatedSessionActivities, { id: selectedItem.id }))
          .filter(Boolean);
        this.$emit('update:selected', selected);
        this.$emit('update:row-count', size(this.paginatedSessionActivities));
      },
    },

    // Catches rows that refresh() edits in place, which the sessionActivities watcher does not see
    linkedAppointmentIds() {
      this.loadLinkedAppointments();
    },

    fixedSessionActivities: {
      deep: true,
      immediate: true,
      handler(fixedSessionActivities) {
        if (!this.useFixedSessionActivities) {
          return;
        }
        this.sessionActivities = fixedSessionActivities || [];
        this.sessionActivitiesPaginationPointer = null;
      },
    },

    selected: {
      handler(newSelected) {
        this.selectedRows = newSelected || [];
      },
      immediate: true,
      deep: true,
    },

    selectedColumns: {
      deep: true,
      immediate: true,
      handler() {
        // Load any required data for selected columns
        if (!chain(this.selectedColumns)
          .intersection(['userUnits', 'integrationStaffRef', 'requiredSignatoriesUserIds', 'userRole'])
          .isEmpty()
          .value()) {
          this.bindUsers(this.userDetails.clinic);
        }
        if (!chain(this.selectedColumns)
          .intersection(['userUnits', 'clientUnit'])
          .isEmpty()
          .value()) {
          this.loadClinicUnits();
        }
        this.loadLinkedAppointments();
      },
    },

    'appliedFilters.userRole': {
      immediate: true,
      handler(value, prevValue) {
        if (!prevValue && value) {
          this.bindUsers(this.userDetails.clinic);
        }
      },
    },

    selectedSessionNoteFields: {
      deep: true,
      immediate: true,
      handler(selectedSessionNoteFields) {
        if (!isEmpty(selectedSessionNoteFields)) {
          this.loadSessionNoteFields(selectedSessionNoteFields);
        }
      },
    },

    selectedSessionNoteTemplateFields: {
      deep: true,
      immediate: true,
      handler(selectedSessionNoteTemplateFields) {
        if (!isEmpty(selectedSessionNoteTemplateFields)) {
          this.loadSessionNoteTemplateFields(selectedSessionNoteTemplateFields);
        }
      },

    },

    selectedRows() {
      this.showApproveButton = this.selectedRows.length > 0
        && this.selectedRows.every((sessionActivity) => sessionActivity.status === 'pending-review')
        && this.userHasPrivilege('sessionActivities/allowApproveChangeRequests')
        && !this.isOnMyActivitiesPage;
    },
  },

  /*
   * The prompt shown in place of the table offers its own way into the filter dialog, and the
   * chips bar that owns the dialog everywhere else sits inside the table this prompt replaces.
   * The note fields it defers are wanted here too, or the criteria arrive incomplete.
   */
  created() {
    this.openFilterDialog = createFilterDialogOpener(() => ({
      filters: this.accessibleFilters,
      filterCriteria: this.filterCriteria,
      filterGroups: this.columnGroups,
      beforeOpen: this.cacheSessionNoteFields,
      onApply: this.onFiltersChanged,
    }));
  }, // created()

  mounted() {
    this.initFilters();
    this.loadSettings();
    this.loadSessionActivities(true);
  },

  beforeUnmount() {
    this.cancelLoadingSessionActivities = true;
  },

  methods: {
    ...mapActions(users, ['bindUsers']),
    ...mapActions(units, ['loadClinicUnits']),

    async refresh(sessionActivityId) {
      if (this.useFixedSessionActivities || !sessionActivityId) {
        return;
      }

      this.isLoadingNewSessions = true;

      try {
        const sessionActivitiesDb = new SessionActivitiesDB();
        const sessionActivity = await sessionActivitiesDb.read(sessionActivityId);

        this.updateNavigationActivityCache(
          sessionActivityId,
          sessionActivity?.isDeleted ? null : sessionActivity,
        );

        const sessionActivityIdx = findIndex(
          this.sessionActivities,
          { id: sessionActivityId },
        );

        if (sessionActivityIdx === -1) {
          return;
        }

        if (!sessionActivity || sessionActivity.isDeleted) {
          this.sessionActivities.splice(sessionActivityIdx, 1);

          this.selectedRows = this.selectedRows.filter(
            ({ id }) => id !== sessionActivityId,
          );
        } else {
          this.sessionActivities.splice(
            sessionActivityIdx,
            1,
            sessionActivity,
          );

          const selectedIdx = findIndex(
            this.selectedRows,
            { id: sessionActivityId },
          );

          if (selectedIdx !== -1) {
            this.selectedRows.splice(selectedIdx, 1, sessionActivity);
          }
        }

        this.$emit('update:selected', this.selectedRows);
      } finally {
        this.isLoadingNewSessions = false;
      }
    }, // refresh()

    async refreshSession(sessionId) {
      if (this.useFixedSessionActivities) {
        return;
      }

      this.isLoadingNewSessions = true;

      const refreshSessionActions = chain(this.sessionActivities)
        .filter({ session: sessionId })
        .map((sessionActivity) => this.refresh(sessionActivity.id))
        .value();
      await Promise.allSettled(refreshSessionActions);

      this.isLoadingNewSessions = false;
    }, // refresh()

    async refreshAll() {
      this.clearNavigationActivitiesCache();
      return this.loadSessionActivities(true);
    }, // refreshAll()

    getOrderedActivities() {
      return this.uniqueFilteredSessionActivities;
    }, // getOrderedActivities()

    async loadNavigationActivitiesForSession(sessionId) {
      if (!sessionId || this.navigationActivitiesBySession[sessionId]) {
        return true;
      }

      try {
        const sessionActivitiesDb = new SessionActivitiesDB();

        const activities = await sessionActivitiesDb.readAll([
          ['clinic', '==', this.userDetails.clinic],
          ['session', '==', sessionId],
          ['isDeleted', '==', false],
        ]);

        this.navigationActivitiesBySession[sessionId] = activities;

        return true;
      } catch (error) {
        console.error('Failed to load navigation activities:', error);
        logEvent(error);

        return false;
      }
    },

    updateNavigationActivityCache(sessionActivityId, sessionActivity) {
      Object.entries(this.navigationActivitiesBySession).forEach(([sessionId, activities]) => {
        const updatedActivities = activities.filter(
          (activity) => activity.id !== sessionActivityId,
        );

        if (sessionActivity && sessionActivity.session === sessionId) {
          updatedActivities.push(sessionActivity);
        }

        this.navigationActivitiesBySession[sessionId] = updatedActivities;
      });
    },

    clearNavigationActivitiesCache() {
      this.navigationActivitiesBySession = {};
    },

    getNavigationActivities() {
      const loadedActivities = Object.values(this.navigationActivitiesBySession)
        .flat();

      return Array.from(
        new Map([
          ...this.uniqueFilteredSessionActivities,
          ...loadedActivities,
        ].map((activity) => [activity.id, activity])).values(),
      );
    },

    getNavigationActivityById(activityId) {
      const loadedActivities = Object.values(this.navigationActivitiesBySession)
        .flat();

      return [
        ...loadedActivities,
        ...this.uniqueFilteredSessionActivities,
      ].find(({ id }) => id === activityId) || null;
    },

    /**
     * Follows the dialog past a page boundary, so the user is left where they were.
     *
     * @param {string} sessionActivityId
     * @return {void}
     * */
    revealActivity(sessionActivityId) {
      const { rowsPerPage } = this.pagination;
      if (!rowsPerPage) {
        return;
      }

      const index = findIndex(this.uniqueFilteredSessionActivities, { id: sessionActivityId });
      if (index < 0) {
        return;
      }

      const page = Math.floor(index / rowsPerPage) + 1;
      if (page !== this.pagination.page) {
        this.pagination.page = page;
      }
    }, // revealActivity()

    canLoadMoreActivities() {
      return !this.useFixedSessionActivities
        && this.sessionActivitiesPaginationPointer !== null;
    }, // canLoadMoreActivities()

    async loadMoreActivities() {
      if (!this.canLoadMoreActivities() || this.isLoadingNewSessions) {
        return;
      }
      await this.loadSessionActivities(false);
    }, // loadMoreActivities()

    onClick(sessionActivity) {
      if (this.rowSelect) {
        this.onSelectRow(sessionActivity);
      } else {
        this.$emit('click', sessionActivity.session, sessionActivity.id, sessionActivity);
      }
    },

    onSelect(selectedSessionActivities) {
      this.selectedRows = selectedSessionActivities;
      this.$emit('update:selected', this.selectedRows);
    },

    onSelectRow(selectedSessionActivity) {
      if (this.isInactive(selectedSessionActivity)) {
        return;
      }

      if (this.selection === 'single') {
        this.selectedRows = [selectedSessionActivity];
      } else {
        this.selectedRows = xor(this.selectedRows, [selectedSessionActivity]);
      }
      this.$emit('update:selected', this.selectedRows);
    },

    onEdit(sessionActivity) {
      this.$emit('edit', sessionActivity.session, sessionActivity.id, sessionActivity);
    },

    onRequest(requestProp) {
      if ((requestProp.pagination.page !== this.pagination.page)
        || (requestProp.pagination.rowsPerPage !== this.pagination.rowsPerPage)) {
        this.pagination.page = requestProp.pagination.page;
        this.pagination.rowsPerPage = requestProp.pagination.rowsPerPage;
        if (size(this.filteredSessionActivities) < requestProp.pagination.page * requestProp.pagination.rowsPerPage) {
          this.loadSessionActivities(false);
        }
      }
    }, // onRequest()

    onOpenMatchToAppointments() {
      const reconciliationActivities = this.selectedRows.filter((sessionActivity) => (
        sessionActivity?.type !== 'form'
      ));

      if (reconciliationActivities.length === 0) {
        return;
      }

      this.openComponentDialog(
        ActivityReconciliationDialog,
        {
          sessionActivities: reconciliationActivities,
        },
        async (numLinkedActivities) => {
          if (numLinkedActivities > 0) {
            await this.refreshAll();
          }
        },
      );
    }, // onOpenMatchToAppointments()

    getAuditVerdictIcon(sessionActivity) {
      return {
        [VERDICTS.PASS]: 'done',
        [VERDICTS.FLAG]: 'flag',
        [VERDICTS.FAIL]: 'priority_high',
      }[sessionActivity.audit?.verdict] || null;
    }, // getAuditVerdictIcon()

    getAuditVerdictColor(sessionActivity) {
      return {
        [VERDICTS.PASS]: 'positive',
        [VERDICTS.FLAG]: 'warning',
        [VERDICTS.FAIL]: 'negative',
      }[sessionActivity.audit?.verdict] || null;
    }, // getAuditVerdictColor()

    getAuditVerdictLabel(sessionActivity) {
      return sessionActivity.audit?.verdict
        ? this.$t(`noteAuditor.verdictOptions.${sessionActivity.audit.verdict}`)
        : '';
    }, // getAuditVerdictLabel()

    getLastAuditTimestamp(sessionActivity) {
      const lastAuditTimestamp = sessionActivity?.auditMeta?.lastAuditTimestamp;

      return lastAuditTimestamp instanceof Timestamp ? lastAuditTimestamp : null;
    }, // getLastAuditTimestamp()

    getIsOutdated(sessionActivity) {
      return isStoredAuditOutdated(sessionActivity);
    }, // getIsOutdated()

    onOpenAIAuditDialog() {
      this.openComponentDialog(
        NoteAuditDialog,
        {
          sessionActivities: this.selectedRows,
        },
        async () => {
          await this.refreshAll();
        },
      );
    }, // onOpenAIAuditDialog()

    async onOpenRecoverActivitiesDialog() {
      this.openComponentDialog(RecoverSessionActivitiesDialog, {}, async () => {
        await this.refreshAll();
      });
    }, // onOpenRecoverActivitiesDialog()

    onAddFilter(filterKey) {
      this.openFilterDialog(filterKey);
    }, // onAddFilter()

    /*
     * The note fields are a Firestore read the table deliberately defers, since most visits never
     * open the filter dialog. The bar awaits this before opening, so the criteria are complete by
     * the time it renders them.
     */
    async cacheSessionNoteFields() {
      if (this.allSessionNoteFieldsCached) {
        return;
      }

      this.$q.loading.show({ delay: 10 });
      try {
        await this.loadAllSessionNoteFields();

        /*
         * loadAllSessionNoteFields reports its own failure and resolves, so the flag is the only
         * signal that the read worked. Throwing is what abandons the open: resolving here would
         * raise the dialog with the note criteria missing, and a filter already set on one could
         * then be neither seen nor edited.
         */
        if (!this.allSessionNoteFieldsCached) {
          throw new Error('Session note fields were not loaded');
        }
      } finally {
        this.$q.loading.hide();
      }
    }, // cacheSessionNoteFields()

    async onFiltersChanged(filters) {
      this.filters = filters;

      this.saveFilter();
      await this.loadSessionActivities(true);

      this.filterSelectedRows();
    }, // onFiltersChanged()

    filterSelectedRows() {
      // If number of rows per page exceeds 100 and there are many activities displayed,
      // we must wait until all activities are downloaded before starting to filter the 'selectedRows'.
      const delay = this.pagination.rowsPerPage > 100
        && this.paginatedSessionActivities.length > 50
        ? 2000
        : 0;

      setTimeout(() => {
        this.selectedRows = this.selectedRows.filter(
          (selectedRow) => (this.paginatedSessionActivities.find(
            (activity) => (selectedRow.id === activity.id),
          ) !== undefined),
        );
      }, delay);

      this.$emit('update:selected', this.selectedRows);
    },

    /**
     * Handles the selection of columns to be displayed in the table. It checks if any of the selected
     * columns require additional data to be loaded (such as session note fields or templates) and
     * loads that data if necessary before opening the column selection dialog. Once the user has made
     * their selection, it updates the selected columns and saves the selection to local storage if applicable.
    * @returns {Promise<void>}
     */
    async onSelectColumns() {
      const cacheFieldsPromises = [];
      if (!this.allSessionNoteFieldsCached) {
        cacheFieldsPromises.push(this.loadAllSessionNoteFields());
      }
      if (!this.allSessionNoteTemplatesCached) {
        cacheFieldsPromises.push(this.loadAllSessionNoteTemplateFields());
      }
      if (cacheFieldsPromises.length > 0) {
        this.$q.loading.show({ delay: 10 });
        await Promise.all(cacheFieldsPromises);
        this.$q.loading.hide();
      }

      const selectionColumns = chain(this.columns)
        .reject({ hideFromSelection: true })
        .map((column) => (
          {
            value: column.name,
            label: column.selectionLabel || column.label || column.exportLabel || '',
            disable: column.mandatory || false,
            group: column.group,
          }
        ))
        .value();

      const modalOptions = {
        options: selectionColumns,
        optionGroupLabels: this.columnGroups,
        selected: this.selectedColumns,
        title: this.$t('common.tableEditColumns'),
        isAbleToReorder: true,
      };

      this.openComponentDialog(
        UniversalColumnSelector,
        modalOptions,
        (newSelectedColumns) => {
          this.selectedColumns = newSelectedColumns;
          if (isArray(newSelectedColumns) && this.localStorageProp) {
            localStorage.setItem(`${this.localStorageProp}Columns`, JSON.stringify(newSelectedColumns));
          }
        },
      );
    },

    onLoadView(tableViewProps) {
      this.selectedColumns = tableViewProps.columns;
      this.pagination = tableViewProps.pagination;
      this.filters = withPersistentFlags(tableViewProps.filters, this.defaultFilters);
      this.loadSessionActivities(true);
    }, // onLoadView()

    async onTransferSelectedNotes() {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t(
          'history.confirmTransferSelectedNotes',
          { integration: this.getIntegrationName(this.connectedClinic.integration) },
        ),
        async () => {
          this.openComponentDialog(
            BulkTransferSessionActivities,
            {
              sessionActivities: this.selectedRows,
            },
            (successfulTransfers) => {
              if (successfulTransfers > 0) {
                this.loadSessionActivities(true);
              }

              this.selectedRows = [];
              this.isSendingSessionNote = false;
            },
          );
        },
      );
    }, // onTransferSelectedNotes()

    async isSessionVisible(sessionActivity) {
      if (!sessionActivity?.session) {
        return true;
      }

      try {
        const sessionsDb = new SessionsDB();
        const session = await sessionsDb.read(sessionActivity.session);

        if (!session || session?.isDeleted) {
          return false;
        }

        return this.hasSupervisorCredentials
        || !(session.limitedVisibility
          && session.limitHistoryVisibility
          && !(get(session, 'visibility', []).includes(this.userDetails.userPrivilege || this.userDetails.role)));
      } catch (err) {
        console.error(`Error checking session visibility: ${err.message}`);
        logEvent(err);
        return false;
      }
    },

    onExportToExcel() {
      const exportItems = isEmpty(this.selectedRows) ? this.sessionActivities : this.selectedRows;
      const columns = chain(this.selectedColumns)
        .map((selectedColumn) => find(this.columns, { name: selectedColumn }))
        .compact()
        .value();

      const workbook = XLSX.utils.book_new();

      const exportColumns = map(
        columns,
        (column) => (column.label || column.exportLabel || ''),
      );

      const exportData = map(
        exportItems,
        (row) => {
          return map(
            columns,
            (column) => {
              let value = null;
              if (isFunction(column.exportFormatExcel)) {
                value = column.exportFormatExcel(get(row, column.field, ''), row);
              } else if (isFunction(column.format)) {
                value = column.format(get(row, column.field, ''), row);
              } else {
                value = get(row, column.field, '');
              }
              return value;
            },
          );
        },
      );

      /* Create worksheet */
      const wsData = concat([exportColumns], exportData);
      const worksheet = XLSX.utils.aoa_to_sheet(wsData, { cellDates: true });

      /* Add the worksheet to the workbook */
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sessions');
      XLSX.writeFile(workbook, 'sessions.xlsx');
    }, // onExportToExcel()

    async onExportSessionToPDF(sessionActivity, includeData) {
      try {
        this.$q.loading.show({ delay: 0 });

        if (!await this.isSessionVisible(sessionActivity)) {
          this.notifyNegative(this.$t('history.exportVisibilityError'));
          return;
        }

        const pdfData = includeData && sessionActivity.session
          ? await this.exportSessionDataToPdf(sessionActivity.session, sessionActivity.client, this.allPrograms)
          : [];
        const contentPages = [
          this.getSessionNotePdfPage(pdfData, sessionActivity.notes, sessionActivity.signatures),
          ...await this.getAddendumPdfPages(sessionActivity),
        ];

        const layoutFieldsData = await this.getLayoutFieldsData({ sessionActivity });
        await this.downloadSessionNotesPdf(contentPages, layoutFieldsData);
      } catch (err) {
        console.error(`Exporting error: ${err.message}`);
        this.notifyNegative(err.message);
        logEvent(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // onExportSessionToPDF()

    async onExportSelectedToPDF(includeData) {
      try {
        this.$q.loading.show({ delay: 0 });

        const contentPageActions = map(
          this.selectedRows,
          async (sessionActivity, idx) => {
            if (!await this.isSessionVisible(sessionActivity)) {
              this.notifyNegative(this.$t('history.exportVisibilityError'));
              return;
            }

            const pdfData = includeData && sessionActivity.session
              ? await this.exportSessionDataToPdf(sessionActivity.session, sessionActivity.client, this.allPrograms)
              : [];

            return [
              this.getSessionNotePdfPageForBulkExport(pdfData, sessionActivity, idx),
              ...await this.getAddendumPdfPages(sessionActivity, idx),
            ];
          },
        );

        const layoutFieldsData = await this.getBulkLayoutFieldsData(this.selectedRows);
        const contentPages = await Promise.all(contentPageActions);
        const filteredContentPages = contentPages.filter(Boolean).flat();
        if (!filteredContentPages.length) {
          return;
        }

        await this.downloadSessionNotesPdf(filteredContentPages, layoutFieldsData);
      } catch (err) {
        console.error(`Exporting error: ${err.message}`);
        this.notifyNegative(err.message);
        logEvent(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // onExportSelectedToPDF()

    onViewAuditTrail(sessionActivity) {
      this.openComponentDialog(ViewAuditTrailDialog, {
        collection: 'sessionActivities',
        resourceId: sessionActivity.id,
      });
    },

    onCopySupportInfo(sessionActivity) {
      copySupportInfoToClipboard({ 'session activity': sessionActivity.id });
    },

    saveFilter() {
      if (this.localStorageProp === null) {
        return;
      }
      const filterJSON = JSON.stringify(this.filters);
      localStorage.setItem(`${this.localStorageProp}Filter`, filterJSON);
    }, // saveFilter()

    initFilters() {
      if (this.localStorageProp !== null) {
        const filterJSON = localStorage.getItem(`${this.localStorageProp}Filter`);
        this.filters = withPersistentFlags(this.jsonParseFilters(filterJSON, this.columns), this.defaultFilters);
      } else {
        this.filters = {};
      }

      if (isEmpty(this.filters)) {
        this.filters = this.defaultFilters;
      }
    }, // initFilters()

    loadSettings() {
      if (this.localStorageProp !== null) {
        const selectedColumns = localStorage.getItem(`${this.localStorageProp}Columns`);
        if (selectedColumns !== null) {
          this.selectedColumns = JSON.parse(selectedColumns);
        }
      }
      if (isEmpty(this.selectedColumns)) {
        this.selectedColumns = this.defaultColumns;
      }
    }, // loadSettings()

    async loadSessionActivities(reset = false, currentAppliedFiltersId = null) {
      if (this.useFixedSessionActivities) {
        // We should only show a fixed set of session activities
        return;
      }

      // Initialize filter ID of appliedFiltersId
      if (!this.appliedFiltersId) {
        this.appliedFiltersId = this.createAppliedFiltersId();
      }

      // Compare values of first and second filter, if they are not equal
      // (as expected after applying the second filter) - return from the loadSessionActivities calling cycle;
      if (!reset && currentAppliedFiltersId && (currentAppliedFiltersId !== this.appliedFiltersId)) {
        return;
      }

      if (!reset && (this.sessionActivitiesPaginationPointer === null)) {
        // No filters set or we have read all sessions
        return;
      }

      if (reset) {
        this.pagination.page = 1;
        this.cancelLoadingSessionActivities = false;
        this.appliedFiltersId = this.createAppliedFiltersId();
        this.clearNavigationActivitiesCache();
      }

      this.isLoadingNewSessions = true;

      try {
        const disjunctionStatements = [];
        const query = [
          ['clinic', '==', this.userDetails.clinic],
        ];
        const statusFilteredItems = chain(this.appliedFilters)
          .get(['status', 'filterStatements'])
          .head()
          .get('filteredItems')
          .value();

        const requiredSignatoriesUserIdsFilter = chain(this.appliedFilters)
          .get(['requiredSignatoriesUserIds', 'filterStatements'])
          .head()
          .get('filteredItems')
          .value();

        if (size(statusFilteredItems) === 1 && size(requiredSignatoriesUserIdsFilter) === 1) {
          query.push(['status', '==', head(statusFilteredItems)]);
          query.push(['requiredSignatoriesUserIds', 'array-contains', head(requiredSignatoriesUserIdsFilter)]);
        } else {
          if (this.fromTime !== null) {
            query.push(['startTimestamp', '>=', this.fromTime]);
          }
          if (this.toTime !== null) {
            query.push(['startTimestamp', '<=', this.toTime]);
          }

          const clientFilteredItems = chain(this.appliedFilters)
            .get(['client', 'filterStatements'])
            .head()
            .get('filteredItems')
            .value();

          let userFilteredItems;
          if (get(this.appliedFilters, ['userName', 'filterProperty']) === 'user') {
            userFilteredItems = chain(this.appliedFilters)
              .get(['userName', 'filterStatements'])
              .head()
              .get('filteredItems')
              .value();
          }

          const typeFilteredItems = chain(this.appliedFilters)
            .get(['type', 'filterStatements'])
            .head()
            .get('filteredItems')
            .value();

          if (size(statusFilteredItems) === 1) {
            query.push(['status', '==', head(statusFilteredItems)]);
          } else if ((size(statusFilteredItems) > 0) && (size(statusFilteredItems) <= 30)) {
            disjunctionStatements.push(['status', 'in', statusFilteredItems]);
          }
          if (size(typeFilteredItems) === 1) {
            query.push(['type', '==', head(typeFilteredItems)]);
          } else if ((size(typeFilteredItems) > 0) && (size(typeFilteredItems) <= 30)) {
            disjunctionStatements.push(['type', 'in', typeFilteredItems]);
          }
          if (size(clientFilteredItems) === 1) {
            query.push(['client', '==', head(clientFilteredItems)]);
          } else if ((size(clientFilteredItems) > 0) && (size(clientFilteredItems) <= 30)) {
            disjunctionStatements.push(['client', 'in', clientFilteredItems]);
          }
          if (size(userFilteredItems) === 1) {
            query.push(['user', '==', head(userFilteredItems)]);
          } else if ((size(userFilteredItems) > 0) && (size(userFilteredItems) <= 30)) {
            disjunctionStatements.push(['user', 'in', userFilteredItems]);
          }
        }

        if (this.isDeletedList) {
          query.push(['isDeleted', '==', true]);
        }

        // Firestore has a limit of 30 disjunctions in a single query
        // Therefore, we must solve the subset product problem
        // to find the best combination of disjunctions that gives us the maximum number of results
        // Ie. the closest to 30 disjunctions we can for all 'in' statements get without exceeding it
        const dp = { 1: [] };
        disjunctionStatements.forEach((disjunctionStatement) => {
          const updates = map(dp, (subset, prod) => {
            const num = disjunctionStatement[2].length;
            const newProd = prod * num;
            return newProd <= 30 && !(newProd in dp)
              ? [newProd, [...subset, disjunctionStatement]]
              : null;
          });

          updates.forEach((update) => {
            if (update) {
              const [prod, subset] = update;
              dp[prod] = subset;
            }
          });
        });

        // Get max key from dp
        const maxKey = chain(dp)
          .keys()
          .map((key) => parseInt(key, 10))
          .max()
          .value();
        if (maxKey) {
          query.push(...dp[maxKey]);
        }

        // Only the self-scheduled chain below passes currentAppliedFiltersId. It runs because a client-side filter
        // left the page short, so a selective filter can need thousands of documents to fill one screen.
        const docsPerQuery = currentAppliedFiltersId ? 500 : 100;

        const sessionActivitiesDb = new SessionActivitiesDB();
        const sessionActivitiesRef = await sessionActivitiesDb.readPaginated(
          query,
          'startTimestamp',
          'desc',
          docsPerQuery,
          reset ? null : this.sessionActivitiesPaginationPointer,
        );

        const existingActivities = sessionActivitiesRef.docs
          .map((sessionDoc) => ({
            ...sessionDoc.data(),
            id: sessionDoc.id,
          }))
          .filter((activity) => (this.isDeletedList || !activity.isDeleted)
              && (this.failedActivityIds.length === 0 || this.failedActivityIds.includes(activity.id)));

        if (reset) {
          // Appointments are cached in two places: here and inside fetchBulkLayoutData. Both must be
          // cleared, or the next load returns the old objects without reading Firestore.
          this.linkedAppointments = {};
          this.layoutDataCache[CATEGORY_IDENTIFIERS.APPOINTMENT].clear();
          this.sessionActivities = existingActivities;
          if (this.selectAll) {
            this.selectedRows = this.paginatedSessionActivities;
            this.$emit('update:selected', this.selectedRows);
          } else if (!isEmpty(this.defaultSelection)) {
            this.selectedRows = filter(
              this.paginatedSessionActivities,
              (sessionActivity) => includes(this.defaultSelection, sessionActivity.id),
            );
            this.$emit('update:selected', this.selectedRows);
          } else {
            this.selectedRows = this.selectedRows
              .map((selectedRow) => find(this.paginatedSessionActivities, { id: selectedRow.id }))
              .filter(Boolean);
            this.$emit('update:selected', this.selectedRows);
          }
        } else if (!sessionActivitiesRef.empty) {
          this.sessionActivities = concat(this.sessionActivities, existingActivities);
        }

        this.sessionActivitiesPaginationPointer = chain(sessionActivitiesRef)
          .get('docs')
          .last()
          .defaultTo(null)
          .value();

        // Appointment source filters match against linked appointments, so load them before counting
        // the filtered rows. If the filters changed meanwhile, the newer load takes over.
        if (this.hasAppointmentSourceFilter) {
          const filtersId = this.appliedFiltersId;
          await this.loadLinkedAppointments();
          if (this.cancelLoadingSessionActivities || (filtersId !== this.appliedFiltersId)) {
            return;
          }
        }

        const numPages = Math.floor(size(this.filteredSessionActivities) / this.pagination.rowsPerPage) + 1;

        // Check if we received the last set of docs
        if (sessionActivitiesRef.size < docsPerQuery) {
          this.pagination.rowsNumber = size(this.filteredSessionActivities);
        } else {
          if ((numPages <= this.pagination.page)
          && ((size(this.filteredSessionActivities) / this.pagination.rowsPerPage) < this.pagination.page)) {
          // If we are on the last page, load more docs
            setTimeout(() => {
              if (!this.cancelLoadingSessionActivities) {
                this.loadSessionActivities(false, currentAppliedFiltersId || this.appliedFiltersId);
              }
            }, 10);
            return;
          }
          this.pagination.rowsNumber = size(this.filteredSessionActivities) + 1;
        }
      } finally {
        this.isLoadingNewSessions = false;
      }
    }, // loadSessionActivities()

    createAppliedFiltersId() {
      if (isEmpty(this.appliedFilters)) {
        return null;
      }

      return Object.values(this.appliedFilters)
        .map((item) => item.id)
        .join('_');
    }, // createAppliedFiltersId

    async loadSessionNoteFields(selectedSessionNoteFields) {
      const uncachedFields = selectedSessionNoteFields
        .filter((fieldId) => !this.cachedSessionNoteFields.some((cachedField) => cachedField.id === fieldId));

      const sessionNoteFieldsDb = new SessionNoteFieldsDB();
      this.$q.loading.show({ delay: 10 });
      try {
        const loadedSessionNoteFields = await sessionNoteFieldsDb.readMultiple(uncachedFields);

        this.cachedSessionNoteFields = chain(this.cachedSessionNoteFields)
          .concat(loadedSessionNoteFields)
          .filter((field) => includes(this.validSessionNoteFieldTypes, field.type))
          .uniqBy('id')
          .value();
      } catch (err) {
        logEvent(err);
        this.notifyNegative(this.$t('common.errors.errorLoadingSessionNoteFields'));
        return;
      } finally {
        this.$q.loading.hide();
      }
    }, // loadSessionNoteFields()

    /**
     * Loads the appointments linked to the activities held in memory. Costs a Firestore read per
     * linked activity, so it only runs while an appointment source column or filter is in use.
     *
     * Calls are queued, so when one resolves every linked appointment is loaded, and overlapping
     * calls never fetch the same appointment twice.
     */
    loadLinkedAppointments() {
      this.linkedAppointmentsQueue = this.linkedAppointmentsQueue
        .then(() => this.fetchMissingLinkedAppointments());
      return this.linkedAppointmentsQueue;
    }, // loadLinkedAppointments()

    async fetchMissingLinkedAppointments() {
      const isInUse = this.hasAppointmentSourceFilter
        || this.selectedColumns.some((column) => column.startsWith(APPOINTMENT_SOURCE_COLUMN_PREFIX));

      if (!isInUse || isEmpty(this.sessionActivities)) {
        return;
      }

      const unloaded = this.sessionActivities
        .filter(({ appointment }) => appointment && !this.linkedAppointments[appointment]);

      if (isEmpty(unloaded)) {
        return;
      }

      this.isLoadingLinkedAppointments = true;
      try {
        const appointments = await this.fetchBulkLayoutData(unloaded, CATEGORY_IDENTIFIERS.APPOINTMENT);
        this.linkedAppointments = (appointments || []).reduce(
          (loaded, appointment) => ({ ...loaded, [appointment.id]: appointment }),
          { ...this.linkedAppointments },
        );
      } catch (err) {
        logEvent(err);
        this.notifyNegative(this.$t('common.errors.errorLoadingLinkedAppointments'));
      } finally {
        this.isLoadingLinkedAppointments = false;
      }
    }, // fetchMissingLinkedAppointments()

    /**
     * An appointment source cell is blank both while its appointment loads and when there is nothing
     * to show, so it renders a skeleton while its own appointment is loading to keep the two apart.
     */
    isLoadingAppointmentSourceCell(column, row) {
      return this.isLoadingLinkedAppointments
        && column.name.startsWith(APPOINTMENT_SOURCE_COLUMN_PREFIX)
        && !!row.appointment
        && !this.linkedAppointments[row.appointment];
    }, // isLoadingAppointmentSourceCell()

    async loadAllSessionNoteFields() {
      const sessionNoteFieldsDb = new SessionNoteFieldsDB();
      try {
        this.cachedSessionNoteFields = await sessionNoteFieldsDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
            ['type', 'in', this.validSessionNoteFieldTypes],
          ],
        );
        this.allSessionNoteFieldsCached = true;
      } catch (err) {
        logEvent(err);
        this.notifyNegative(this.$t('common.errors.errorLoadingSessionNoteFields'));
      }
    }, // loadAllSessionNoteFields()

    /**
     * Loads session note template fields based on the selected session note template field IDs. It first checks
     * which of the selected session note template fields are not already cached, then retrieves the corresponding
     * session note templates for those fields, extracts the fields from the templates, and updates the
     * cached session note template fields with the newly loaded fields.
     *
     * @param selectedSessionNoteTemplateFields {array} - An array of selected session note
     * template field IDs in the format 'templateId.fieldId'.
     */
    async loadSessionNoteTemplateFields(selectedSessionNoteTemplateFields) {
      const uncachedNoteTemplateFields = selectedSessionNoteTemplateFields
        .filter((templateFieldId) => !this.cachedSessionNoteTemplateFields
          .some((cachedTemplateField) => cachedTemplateField.id === templateFieldId));

      const uncachedTemplateIds = chain(uncachedNoteTemplateFields)
        .map((fieldId) => fieldId.split('.')[0])
        .uniq()
        .value();

      const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
      try {
        this.$q.loading.show({ delay: 10 });

        const sessionNoteTemplates = await sessionNoteTemplatesDb.readMultiple(uncachedTemplateIds);
        const sessionNoteTemplateFields = chain(sessionNoteTemplates)
          .flatMap((template) => template.fields.map((field) => ({
            ...field,
            id: `${template.id}.${field.id}`,
            templateId: template.id,
            templateName: template.noteTemplateName,
            sessionActivityTypes: template.sessionActivityTypes,
            field,
          })))
          .filter(({ field }) => includes(this.validSessionNoteFieldTypes, field.type))
          .value();

        this.cachedSessionNoteTemplateFields = chain(this.cachedSessionNoteTemplateFields)
          .concat(sessionNoteTemplateFields)
          .uniqBy('id')
          .value();
      } catch (err) {
        logEvent(err);
        this.notifyNegative(this.$t('common.errors.errorLoadingFormDataFields'));
      } finally {
        this.$q.loading.hide();
      }
    }, // loadSessionNoteTemplateFields()

    /**
     * Loads all session note template fields for the clinic. It retrieves all session note
     * templates that are associated with the clinic and extracts the fields from those templates.
     *
     * We currently only let the user select session note template fields that are associated with
     * forms. This is to allow users to download results from forms and assessments where it doesn't
     * make sense to have all fields saved for re-use.
     *
     * @returns {Promise<void>}
     */
    async loadAllSessionNoteTemplateFields() {
      const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
      try {
        const sessionNoteTemplates = await sessionNoteTemplatesDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
            ['sessionActivityTypes', 'array-contains-any', this.validSessionActivityTypes],
          ],
        );
        this.cachedSessionNoteTemplateFields = chain(sessionNoteTemplates)
          .flatMap((template) => template.fields.map((field) => ({
            ...field,
            id: `${template.id}.${field.id}`,
            templateId: template.id,
            templateName: template.noteTemplateName,
            sessionActivityTypes: template.sessionActivityTypes,
            field,
          })))
          .filter(({ field }) => includes(this.validSessionNoteFieldTypes, field.type))
          .value();
        this.allSessionNoteTemplatesCached = true;
      } catch (err) {
        logEvent(err);
        this.notifyNegative(this.$t('common.errors.errorLoadingFormDataFields'));
      }
    }, // loadAllSessionNoteTemplateFields()

    formatSessionNoteField(value, type, field) {
      if (type === 'date') {
        return value instanceof Timestamp ? this.getLocaleDateString(value) : '';
      }
      if (type === 'time') {
        return value instanceof Timestamp ? this.formatTimeOfDay(value.toDate()) : '';
      }
      if (type === 'score' && field?.scoreConfig?.scoreType === 'explicit') {
        return field.scoreConfig.explicitValues.find((score) => score.value === value)?.label;
      }
      if (Array.isArray(value)) {
        return value.join(', ');
      }

      return value;
    }, // formatSessionNoteField()

    formatTimeOfDay(date) {
      return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }, // formatTimeOfDay()

    formatAppointmentSourceField(value, type) {
      if (value instanceof Date) {
        return type === 'time' ? this.formatTimeOfDay(value) : value.toLocaleDateString();
      }
      if (Array.isArray(value)) {
        return value.join(', ');
      }

      return value ?? '';
    }, // formatAppointmentSourceField()

    formatAppointmentSourceFieldExcel(value, type) {
      if (value instanceof Date) {
        // XLSX writes Dates with a date-only number format, which would drop the time of day
        return type === 'time' ? this.formatTimeOfDay(value) : value;
      }
      if (Array.isArray(value)) {
        return value.join(', ');
      }

      return value ?? '';
    }, // formatAppointmentSourceFieldExcel()

    formatSessionNoteFieldExcel(value, type) {
      if (type === 'time') {
        // XLSX writes Dates with a date-only number format, which would drop the time of day
        return value instanceof Timestamp ? this.formatTimeOfDay(value.toDate()) : '';
      }
      if (type === 'date') {
        return value instanceof Timestamp ? this.getLocalToTimezoneOffsetDate(value) : '';
      }
      if (Array.isArray(value)) {
        return value.join(', ');
      }

      return value;
    }, // formatSessionNoteFieldExcel()

    getFilterTypeForSessionNoteField(type, allowMultipleOptions) {
      switch (type) {
        case 'select': {
          if (allowMultipleOptions) {
            return filterPropertyTypes.ArrayInArray;
          }

          return filterPropertyTypes.StringInArray;
        }
        case 'date': {
          return filterPropertyTypes.Date;
        }
        case 'number':
        case 'score': {
          return filterPropertyTypes.Number;
        }
        default: {
          return filterPropertyTypes.StringMatch;
        }
      }
    }, // getFilterTypeForSessionNoteField()

    getFilterItemsForSessionNoteField(field) {
      switch (field.type) {
        case 'select': {
          return chain(field.options)
            .map((option) => ({
              value: option,
              label: option,
            }))
            .value();
        }
        default: {
          return null;
        }
      }
    }, // getFilterItemsForSessionNoteField()

    isInactive(sessionActivity) {
      if (typeof this.disableCheck === 'function') {
        return this.disableCheck(sessionActivity);
      }
      return false;
    },

    async openBulkApprovalModal() {
      this.openComponentDialog(
        ChangeRequestConfirmationModal,
        {
          approvalRequestsAmount: this.selectedRows.filter((row) => row.status === 'pending-review').length,
          hasRejectedChangeRequest: this.selectedRows.some((row) => row.changeRequest?.rejectionReason),
        },
        async () => {
          this.selectedRows.forEach((sessionActivity) => {
            const sessionNotesFieldData = {
              ...sessionActivity.sessionNotesFieldData,
            };

            const requestedChanges = sessionActivity.changeRequest.fields.reduce((acc, requestField) => {
              Object.entries(requestField).forEach(([key, value]) => { acc[key] = value; });
              return acc;
            }, {});

            const changeRequestKeys = Object.keys(requestedChanges);

            const mappedFieldData = this.mapChangeRequestData(requestedChanges);

            sessionNotesFieldData.session = {
              ...sessionNotesFieldData.session,
              ...mappedFieldData,
            };

            const status = sessionActivity.requiredSignatories?.length > 0 ? 'pending-signatures' : 'closed';

            const { updatedSessionNoteFields, updateSessionActivity } = this.getUpdatedSessionNoteFields(
              sessionActivity,
              this.getFieldDataForApproval(sessionNotesFieldData),
            );

            if (updatedSessionNoteFields.session?.sessionDuration) {
              changeRequestKeys.push('sessionDuration');
            }

            const notes = this.getRenderedNotesWithChangeRequest(
              sessionActivity,
              updatedSessionNoteFields,
              changeRequestKeys,
            );

            const sessionActivityChanges = {
              id: sessionActivity.id,
              status,
              sessionNotesFieldData,
              notes,
              ...updateSessionActivity,
              changeRequest: {
                ...sessionActivity.changeRequest,
                status: 'approved',
              },
            };

            if (sessionActivity.changeRequest.rejectionReason) {
              sessionActivityChanges.changeRequest.rejectionReason = null;
            }

            this.$emit('change-request-bulk-approve', sessionActivityChanges);
          });

          await this.refreshAll();
          this.selectedRows = [];
        },
      );
    }, // openBulkApprovalModal()

    isChangeRequestReasonsTooLong(props) {
      return ['requestReason', 'rejectionReason'].includes(props.col.name) && props.value.length > 20;
    }, // isChangeRequestReasonsTooLong

    allowSendWithoutLinkedAppointments(activityType) {
      return !!this.activityTypesSettings[activityType]?.sendStaffActivitiesWithoutLinkedAppointment;
    },

  },
};
</script>

<style lang="scss" scoped>

// Sits on the status icon's top-right corner; the white ring keeps it apart from the icon.
// The hex fallbacks apply when no design-system theme is set.
.addendum-badge {
  position: absolute;
  top: -7px;
  inset-inline-start: 13px;
  display: inline-flex;
  padding: 3px 5px;
  border-radius: 5px;
  box-shadow: 0 0 0 2px white;
  line-height: 1;

  &--pending {
    background: var(--app-warning-container, #FFEBDC);
    color: var(--app-on-warning-container, #6B3410);
  }

  &--complete {
    background: var(--app-primary-container, #D0EAFC);
    color: var(--app-on-primary-container, #0C5990);
  }
}

.virtual-scroll-height {
  height: 85vh;
}

/*
  @see quasar sticky header https://quasar.dev/vue-components/table#sticky-header-column
  @see deep pseudo-class https://vuejs.org/api/sfc-css-features.html#scoped-css
 */
.sticky-header {
  :deep(.q-table__top),
  :deep(.q-table__bottom),
  :deep(thead) tr:first-child th {
    /* bg color is important for th; just specify one */
    background-color: #ffffff;
  }

  :deep(thead) tr th {
    position: sticky;
    z-index: 1;
  }

  :deep(thead) tr:first-child th {
    top: 0;
  }

  /* prevent scrolling behind sticky top row on focus */
  :deep(tbody) {
    /* height of all previous header rows */
    scroll-margin-top: 55px
  }

  &.q-table--loading :deep(thead) tr:last-child th {
    /* height of all previous header rows */
    top: 55px;
  }

}
</style>
